/**
 * RefactorReportView Component
 *
 * Interactive visualization dashboard for Code Refactor analysis results.
 * Features:
 * - 5 D3 charts with tooltips, click interactions, animations
 * - Cross-chart filtering
 * - Data grid with search, filter, and export
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import * as d3 from 'd3'
import {
  ArrowLeft, Loader2, AlertCircle,
  PieChart, BarChart3, Network, Target, ListChecks, Table2,
  Download, Search, Filter, X, ChevronDown, ChevronUp,
  FileJson, FileSpreadsheet, Activity, TrendingUp, AlertTriangle,
  CheckCircle2, Clock, Layers, Code2, Zap, Gauge, Rocket,
  ArrowRight, Shield, Sparkles, Brain, PanelLeftClose
} from 'lucide-react'
import './RefactorReportView.css'
import { getAPIBaseUrl } from '../../environment.js'
import WorkbenchShell from '../workbench/WorkbenchShell'

const API_BASE_URL = getAPIBaseUrl()

/**
 * Chart type configuration with style variants
 */
const CHART_TYPES = {
  'executive-insights': {
    label: 'Insights',
    icon: Brain,
    description: 'Executive transformation insights and readiness score',
    styles: [],
    defaultStyle: null,
  },
  'recipe-summary': {
    label: 'Recipes',
    icon: PieChart,
    description: 'Distribution of refactoring recipe types',
    styles: [
      { id: 'donut', label: 'Donut Chart' },
      { id: 'horizontal-bar', label: 'Horizontal Bar' },
      { id: 'radial-bar', label: 'Radial Bar' },
    ],
    defaultStyle: 'donut',
  },
  'method-complexity': {
    label: 'Complexity',
    icon: BarChart3,
    description: 'High complexity methods requiring attention',
    styles: [
      { id: 'lollipop', label: 'Lollipop Chart' },
      { id: 'horizontal-bar', label: 'Horizontal Bar' },
      { id: 'diverging', label: 'Diverging Bar' },
    ],
    defaultStyle: 'lollipop',
  },
  'method-grouping': {
    label: 'Groupings',
    icon: Network,
    description: 'COBOL prefix groupings visualization',
    styles: [
      { id: 'sunburst', label: 'Sunburst' },
      { id: 'circle-pack', label: 'Circle Packing' },
      { id: 'treemap', label: 'Treemap' },
    ],
    defaultStyle: 'sunburst',
  },
  'confidence-risk': {
    label: 'Risk Matrix',
    icon: Target,
    description: 'Recipes plotted by confidence vs risk',
    styles: [
      { id: 'bubble', label: 'Bubble Chart' },
      { id: 'scatter', label: 'Scatter Plot' },
      { id: 'heatmap', label: 'Heatmap Grid' },
    ],
    defaultStyle: 'bubble',
  },
  'action-priority': {
    label: 'Actions',
    icon: ListChecks,
    description: 'Action items by priority',
    styles: [
      { id: 'horizontal-stacked', label: 'Stacked Bar' },
      { id: 'treemap', label: 'Treemap' },
      { id: 'timeline', label: 'Priority Timeline' },
    ],
    defaultStyle: 'horizontal-stacked',
  },
  'details-grid': {
    label: 'Data Grid',
    icon: Table2,
    description: 'Searchable data grid with export',
    styles: [],
    defaultStyle: null,
  },
}

/**
 * Professional color palette
 */
const COLORS = {
  primary: ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef'],
  success: ['#10b981', '#34d399', '#6ee7b7'],
  warning: ['#f59e0b', '#fbbf24', '#fcd34d'],
  danger: ['#ef4444', '#f87171', '#fca5a5'],
  cool: ['#06b6d4', '#22d3ee', '#67e8f9'],
  chart: ['#6366f1', '#22d3ee', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#f97316'],
}

/**
 * Shared Tooltip Component
 */
function Tooltip({ content, position, visible }) {
  if (!visible || !content) return null

  return (
    <div
      className="chart-tooltip"
      style={{
        left: position.x,
        top: position.y,
        opacity: visible ? 1 : 0,
      }}
    >
      {content}
    </div>
  )
}

/**
 * Main RefactorReportView component
 */
function RefactorReportView({
  onBack,
  phaseName = 'Refactor',
  analysisContext = null,
}) {
  const [activeChart, setActiveChart] = useState('executive-insights')
  const [recipesData, setRecipesData] = useState(null)
  const [aiData, setAiData] = useState(null)
  const [patternsData, setPatternsData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Chart style selection per chart type
  const [chartStyles, setChartStyles] = useState({
    'recipe-summary': 'donut',
    'method-complexity': 'lollipop',
    'method-grouping': 'sunburst',
    'confidence-risk': 'bubble',
    'action-priority': 'horizontal-stacked',
  })

  // Cross-chart filtering
  const [selectedRecipeType, setSelectedRecipeType] = useState(null)
  const [selectedClass, setSelectedClass] = useState(null)
  const [selectedPriority, setSelectedPriority] = useState(null)

  // Sidebar collapse state (chart nav left rail)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  // Tooltip state
  const [tooltip, setTooltip] = useState({ visible: false, content: null, position: { x: 0, y: 0 } })

  // Handle chart style change
  const handleStyleChange = useCallback((chartType, styleId) => {
    setChartStyles(prev => ({ ...prev, [chartType]: styleId }))
  }, [])

  const showTooltip = useCallback((content, event) => {
    setTooltip({
      visible: true,
      content,
      position: { x: event.pageX + 10, y: event.pageY - 10 }
    })
  }, [])

  const hideTooltip = useCallback(() => {
    setTooltip({ visible: false, content: null, position: { x: 0, y: 0 } })
  }, [])

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSelectedRecipeType(null)
    setSelectedClass(null)
    setSelectedPriority(null)
  }, [])

  const hasActiveFilters = selectedRecipeType || selectedClass || selectedPriority

  /**
   * Fetch report data
   */
  const fetchReportData = useCallback(async () => {
    if (!analysisContext?.jobId) {
      setError('No job ID available. Run Code Refactor first.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const jobId = analysisContext.jobId
      const base = `${API_BASE_URL}/coderefactor/${jobId}/results/json`

      const [recipesRes, aiRes, patternsRes] = await Promise.allSettled([
        fetch(`${base}/refactor_recipes.json`).then(r => r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))),
        fetch(`${base}/ai_analysis.json`).then(r => r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))),
        fetch(`${base}/rule_patterns.json`).then(r => r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))),
      ])

      const recipes = recipesRes.status === 'fulfilled' ? recipesRes.value : null
      const ai = aiRes.status === 'fulfilled' ? aiRes.value : null
      const patterns = patternsRes.status === 'fulfilled' ? patternsRes.value : null

      setRecipesData(recipes)
      setAiData(ai)
      setPatternsData(patterns)

      if (!recipes && !ai && !patterns) {
        setError(`Failed to load refactor artifacts. Job ID: ${jobId}`)
      }
    } catch (err) {
      setError(`Failed to load report: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }, [analysisContext?.jobId])

  useEffect(() => {
    fetchReportData()
  }, [fetchReportData])

  const handleRefresh = useCallback(() => {
    fetchReportData()
  }, [fetchReportData])

  // ── WorkbenchShell slot variables ────────────────────────────────────────

  const wbHeaderIcon = <BarChart3 className="rv-header-icon" size={16} />

  const wbContextTag = `${analysisContext?.className || 'Application'} · Analysis Dashboard`

  const wbHeaderActions = (
    <div className="rrv-header-actions">
      {hasActiveFilters && (
        <button className="rrv-clear-filters-btn" onClick={clearFilters} title="Clear all filters">
          <X size={14} />
          Clear Filters
        </button>
      )}
      <button
        className="rrv-refresh-btn"
        onClick={handleRefresh}
        disabled={loading}
        title="Refresh report data"
      >
        <Activity size={14} />
      </button>
    </div>
  )

  const wbStatusBar = hasActiveFilters ? (
    <div className="rrv-filters-bar rv-status-bar rv-status-bar--running">
      <Filter size={13} />
      <span className="rv-sb-text">Active Filters:</span>
      {selectedRecipeType && (
        <span className="rrv-filter-tag">
          Type: {selectedRecipeType}
          <button onClick={() => setSelectedRecipeType(null)}><X size={11} /></button>
        </span>
      )}
      {selectedClass && (
        <span className="rrv-filter-tag">
          Class: {selectedClass}
          <button onClick={() => setSelectedClass(null)}><X size={11} /></button>
        </span>
      )}
      {selectedPriority && (
        <span className="rrv-filter-tag">
          Priority: {selectedPriority}
          <button onClick={() => setSelectedPriority(null)}><X size={11} /></button>
        </span>
      )}
    </div>
  ) : null

  const wbLeftRail = (
    <div className="rrv-chart-nav">
      {/* Rail header: label + collapse button (mirrors Pre-Flight sidebar behaviour) */}
      <div className="rrv-chart-nav-header">
        <span className="rrv-chart-nav-label">CHARTS</span>
        <button
          className="rrv-chart-nav-collapse-btn"
          onClick={() => setSidebarCollapsed(true)}
          title="Collapse panel"
        >
          <PanelLeftClose size={14} />
        </button>
      </div>
      {Object.entries(CHART_TYPES).map(([type, config]) => {
        const Icon = config.icon
        return (
          <button
            key={type}
            className={`rrv-chart-nav-btn${activeChart === type ? ' active' : ''}`}
            onClick={() => setActiveChart(type)}
            title={config.description}
          >
            <Icon size={15} />
            <span>{config.label}</span>
          </button>
        )
      })}
    </div>
  )

  const wbCenterContent = (
    <div className="rrv-report-content">
      {loading && (
        <div className="loading-state">
          <Loader2 size={32} className="spin" />
          <p>Loading refactor report...</p>
        </div>
      )}

      {error && !loading && (
        <div className="error-state">
          <AlertCircle size={32} />
          <h3>Error Loading Report</h3>
          <p>{error}</p>
          <button onClick={handleRefresh}>Try Again</button>
        </div>
      )}

      {!loading && !error && (recipesData || aiData || patternsData) && (
        <div className="dashboard-layout">
          {/* Main Chart Area */}
          <div className="chart-container">
            {activeChart === 'executive-insights' && (
              <ExecutiveInsightsTab
                recipesData={recipesData}
                aiData={aiData}
                patternsData={patternsData}
                showTooltip={showTooltip}
                hideTooltip={hideTooltip}
              />
            )}
            {activeChart === 'recipe-summary' && (
              <RecipeSummaryChart
                data={recipesData}
                selectedType={selectedRecipeType}
                onSelectType={setSelectedRecipeType}
                showTooltip={showTooltip}
                hideTooltip={hideTooltip}
                chartStyle={chartStyles['recipe-summary']}
                onStyleChange={(style) => handleStyleChange('recipe-summary', style)}
                availableStyles={CHART_TYPES['recipe-summary'].styles}
              />
            )}
            {activeChart === 'method-complexity' && (
              <MethodComplexityChart
                data={patternsData}
                recipesData={recipesData}
                selectedClass={selectedClass}
                showTooltip={showTooltip}
                hideTooltip={hideTooltip}
                chartStyle={chartStyles['method-complexity']}
                onStyleChange={(style) => handleStyleChange('method-complexity', style)}
                availableStyles={CHART_TYPES['method-complexity'].styles}
              />
            )}
            {activeChart === 'method-grouping' && (
              <MethodGroupingChart
                data={patternsData}
                showTooltip={showTooltip}
                hideTooltip={hideTooltip}
                chartStyle={chartStyles['method-grouping']}
                onStyleChange={(style) => handleStyleChange('method-grouping', style)}
                availableStyles={CHART_TYPES['method-grouping'].styles}
              />
            )}
            {activeChart === 'confidence-risk' && (
              <ConfidenceRiskChart
                data={recipesData}
                selectedType={selectedRecipeType}
                onSelectType={setSelectedRecipeType}
                showTooltip={showTooltip}
                hideTooltip={hideTooltip}
                chartStyle={chartStyles['confidence-risk']}
                onStyleChange={(style) => handleStyleChange('confidence-risk', style)}
                availableStyles={CHART_TYPES['confidence-risk'].styles}
              />
            )}
            {activeChart === 'action-priority' && (
              <ActionPriorityChart
                data={aiData}
                selectedPriority={selectedPriority}
                onSelectPriority={setSelectedPriority}
                showTooltip={showTooltip}
                hideTooltip={hideTooltip}
                chartStyle={chartStyles['action-priority']}
                onStyleChange={(style) => handleStyleChange('action-priority', style)}
                availableStyles={CHART_TYPES['action-priority'].styles}
              />
            )}
            {activeChart === 'details-grid' && (
              <DetailsGrid
                recipesData={recipesData}
                aiData={aiData}
                patternsData={patternsData}
                selectedRecipeType={selectedRecipeType}
                selectedClass={selectedClass}
                selectedPriority={selectedPriority}
              />
            )}
          </div>

          {/* Side Panel - hide for data grid and executive insights */}
          {activeChart !== 'details-grid' && activeChart !== 'executive-insights' && (
            <SidePanel
              recipesData={recipesData}
              aiData={aiData}
              patternsData={patternsData}
              activeChart={activeChart}
              selectedRecipeType={selectedRecipeType}
              selectedPriority={selectedPriority}
            />
          )}
        </div>
      )}

      {!loading && !error && !recipesData && !aiData && !patternsData && (
        <div className="no-data-state">
          <AlertCircle size={32} />
          <h3>No Report Data Available</h3>
          <p>Run the Code Refactor flow to generate the refactoring report.</p>
          <p className="debug-info">Job ID: {analysisContext?.jobId || 'Not set'}</p>
          <button className="rrv-back-to-workflow-btn" onClick={onBack}>
            <ArrowLeft size={18} />
            Back to Workflow
          </button>
        </div>
      )}
    </div>
  )

  return (
    <WorkbenchShell
      onBack={onBack}
      backLabel="Back to Workflow"
      headerIcon={wbHeaderIcon}
      title={`${phaseName}: Refactoring Report`}
      contextTag={wbContextTag}
      headerActions={wbHeaderActions}
      statusBar={wbStatusBar}
      leftRail={wbLeftRail}
      sidebarCollapsed={sidebarCollapsed}
      onSidebarCollapse={() => setSidebarCollapsed(true)}
      onSidebarExpand={() => setSidebarCollapsed(false)}
      sidebarLabel="CHARTS"
      sidebarMinSize={140}
      sidebarMaxSize={210}
      sidebarPreferredSize={170}
      centerContent={wbCenterContent}
    >
      {/* D3 tooltip — position: fixed, safe anywhere in DOM */}
      <Tooltip {...tooltip} />
    </WorkbenchShell>
  )
}

/**
 * Executive Insights Tab - Data Storytelling Dashboard
 * Provides high-level transformation insights and modernization readiness scoring
 */
function ExecutiveInsightsTab({ recipesData, aiData, patternsData, showTooltip, hideTooltip }) {
  const gaugeRef = useRef()
  const roadmapRef = useRef()
  const impactRef = useRef()

  // Compute transformation metrics
  const metrics = useMemo(() => {
    const recipes = recipesData?.recipes || []
    const patterns = patternsData?.patterns || []
    const actions = aiData?.recommendations || []

    const totalRecipes = recipes.length
    const highRisk = recipes.filter(r => r.priority === 'high').length
    const mediumRisk = recipes.filter(r => r.priority === 'medium').length
    const lowRisk = recipes.filter(r => r.priority === 'low').length
    const avgConfidence = totalRecipes > 0
      ? recipes.reduce((sum, r) => sum + (r.confidence || 0), 0) / totalRecipes
      : 0

    const complexityPatterns = patterns.filter(p => p.pattern_type === 'high_complexity_method')
    const totalComplexity = complexityPatterns.reduce((sum, p) => sum + (p.evidence?.complexity || 0), 0)
    const avgComplexity = complexityPatterns.length > 0 ? totalComplexity / complexityPatterns.length : 0

    const criticalActions = actions.filter(a => a.priority === 'high').length
    const importantActions = actions.filter(a => a.priority === 'medium').length
    const niceToHaveActions = actions.filter(a => a.priority === 'low').length

    // Compute readiness score (0-100)
    let readinessScore = 50 // Base score

    // Confidence factor (+30 max)
    readinessScore += avgConfidence * 30

    // Low risk bonus (+15 max)
    if (totalRecipes > 0) {
      readinessScore += (lowRisk / totalRecipes) * 15
    }

    // High risk penalty (-20 max)
    if (totalRecipes > 0) {
      readinessScore -= (highRisk / totalRecipes) * 20
    }

    // Complexity penalty (-15 max)
    if (avgComplexity > 20) {
      readinessScore -= Math.min(15, (avgComplexity - 20) * 0.5)
    }

    // Critical actions penalty (-10 max)
    readinessScore -= Math.min(10, criticalActions * 2)

    readinessScore = Math.max(0, Math.min(100, readinessScore))

    // Get readiness level
    let readinessLevel, readinessColor
    if (readinessScore >= 80) {
      readinessLevel = 'Excellent'
      readinessColor = '#9ece6a'
    } else if (readinessScore >= 60) {
      readinessLevel = 'Good'
      readinessColor = '#7aa2f7'
    } else if (readinessScore >= 40) {
      readinessLevel = 'Moderate'
      readinessColor = '#e0af68'
    } else {
      readinessLevel = 'Needs Work'
      readinessColor = '#f7768e'
    }

    // Compute projected impact
    const estimatedLinesReduced = totalRecipes * 15 // Average 15 lines per recipe
    const estimatedComplexityReduction = complexityPatterns.length * 5 // 5 points per fix
    const estimatedTimeToModernize = Math.ceil((criticalActions * 4 + importantActions * 2 + niceToHaveActions) / 5) // Days estimate

    return {
      totalRecipes,
      highRisk,
      mediumRisk,
      lowRisk,
      avgConfidence,
      complexityPatterns: complexityPatterns.length,
      avgComplexity,
      criticalActions,
      importantActions,
      niceToHaveActions,
      totalActions: actions.length,
      readinessScore: Math.round(readinessScore),
      readinessLevel,
      readinessColor,
      estimatedLinesReduced,
      estimatedComplexityReduction,
      estimatedTimeToModernize,
    }
  }, [recipesData, aiData, patternsData])

  // Render gauge chart
  useEffect(() => {
    if (!gaugeRef.current) return

    const svg = d3.select(gaugeRef.current)
    svg.selectAll('*').remove()

    const width = 220
    const height = 140
    const radius = 90
    const cx = width / 2
    const cy = height - 20

    svg.attr('width', width).attr('height', height)

    // Background arc
    const bgArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2)
      .endAngle(Math.PI / 2)

    svg.append('path')
      .attr('d', bgArc())
      .attr('transform', `translate(${cx},${cy})`)
      .attr('fill', '#2a2b3d')

    // Score arc with gradient
    const scoreAngle = -Math.PI / 2 + (metrics.readinessScore / 100) * Math.PI
    const scoreArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2)
      .cornerRadius(4)

    // Create gradient
    const defs = svg.append('defs')
    const gradient = defs.append('linearGradient')
      .attr('id', 'gauge-gradient')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '100%').attr('y2', '0%')
    gradient.append('stop').attr('offset', '0%').attr('stop-color', '#f7768e')
    gradient.append('stop').attr('offset', '40%').attr('stop-color', '#e0af68')
    gradient.append('stop').attr('offset', '70%').attr('stop-color', '#7aa2f7')
    gradient.append('stop').attr('offset', '100%').attr('stop-color', '#9ece6a')

    // Animated score arc
    svg.append('path')
      .attr('transform', `translate(${cx},${cy})`)
      .attr('fill', 'url(#gauge-gradient)')
      .transition()
      .duration(1500)
      .ease(d3.easeElasticOut.amplitude(1).period(0.4))
      .attrTween('d', () => {
        const interpolate = d3.interpolate(-Math.PI / 2, scoreAngle)
        return t => scoreArc.endAngle(interpolate(t))()
      })

    // Score text
    svg.append('text')
      .attr('x', cx)
      .attr('y', cy - 35)
      .attr('text-anchor', 'middle')
      .attr('fill', metrics.readinessColor)
      .attr('font-size', '36px')
      .attr('font-weight', '700')
      .text('0')
      .transition()
      .duration(1500)
      .tween('text', function() {
        const i = d3.interpolateNumber(0, metrics.readinessScore)
        return function(t) {
          this.textContent = Math.round(i(t))
        }
      })

    // Level label
    svg.append('text')
      .attr('x', cx)
      .attr('y', cy - 8)
      .attr('text-anchor', 'middle')
      .attr('fill', metrics.readinessColor)
      .attr('font-size', '14px')
      .attr('font-weight', '600')
      .text(metrics.readinessLevel)
      .style('opacity', 0)
      .transition()
      .delay(800)
      .duration(500)
      .style('opacity', 1)

  }, [metrics])

  // Render transformation roadmap
  useEffect(() => {
    if (!roadmapRef.current) return

    const svg = d3.select(roadmapRef.current)
    svg.selectAll('*').remove()

    const width = 500
    const height = 120
    const margin = { top: 20, right: 40, bottom: 30, left: 40 }

    svg.attr('width', width).attr('height', height)

    const phases = [
      { id: 1, label: 'Analysis', status: 'complete', color: '#9ece6a', items: metrics.totalRecipes },
      { id: 2, label: 'Critical Fixes', status: metrics.criticalActions > 0 ? 'active' : 'complete', color: '#f7768e', items: metrics.criticalActions },
      { id: 3, label: 'Important', status: 'pending', color: '#e0af68', items: metrics.importantActions },
      { id: 4, label: 'Optimization', status: 'pending', color: '#7aa2f7', items: metrics.niceToHaveActions },
      { id: 5, label: 'Complete', status: 'pending', color: '#9ece6a', items: 0 },
    ]

    const phaseWidth = (width - margin.left - margin.right) / phases.length
    const y = height / 2

    const g = svg.append('g').attr('transform', `translate(${margin.left},0)`)

    // Timeline line
    g.append('line')
      .attr('x1', 0)
      .attr('y1', y)
      .attr('x2', 0)
      .attr('y2', y)
      .attr('stroke', '#3b4261')
      .attr('stroke-width', 4)
      .attr('stroke-linecap', 'round')
      .transition()
      .duration(800)
      .attr('x2', width - margin.left - margin.right)

    // Phase nodes
    phases.forEach((phase, i) => {
      const x = i * phaseWidth + phaseWidth / 2

      // Node circle
      g.append('circle')
        .attr('cx', x)
        .attr('cy', y)
        .attr('r', 0)
        .attr('fill', phase.status === 'complete' ? phase.color : phase.status === 'active' ? '#24283b' : '#1a1b26')
        .attr('stroke', phase.color)
        .attr('stroke-width', phase.status === 'active' ? 3 : 2)
        .transition()
        .duration(600)
        .delay(i * 150)
        .attr('r', 16)

      // Check mark for complete
      if (phase.status === 'complete') {
        g.append('text')
          .attr('x', x)
          .attr('y', y + 5)
          .attr('text-anchor', 'middle')
          .attr('fill', 'white')
          .attr('font-size', '14px')
          .text('✓')
          .style('opacity', 0)
          .transition()
          .delay(i * 150 + 400)
          .duration(300)
          .style('opacity', 1)
      } else if (phase.status === 'active') {
        // Pulsing effect for active
        g.append('circle')
          .attr('cx', x)
          .attr('cy', y)
          .attr('r', 16)
          .attr('fill', 'none')
          .attr('stroke', phase.color)
          .attr('stroke-width', 2)
          .style('opacity', 0.5)
          .transition()
          .duration(1500)
          .ease(d3.easeLinear)
          .attr('r', 24)
          .style('opacity', 0)
          .on('end', function repeat() {
            d3.select(this)
              .attr('r', 16)
              .style('opacity', 0.5)
              .transition()
              .duration(1500)
              .ease(d3.easeLinear)
              .attr('r', 24)
              .style('opacity', 0)
              .on('end', repeat)
          })

        // Count in node
        g.append('text')
          .attr('x', x)
          .attr('y', y + 5)
          .attr('text-anchor', 'middle')
          .attr('fill', phase.color)
          .attr('font-size', '12px')
          .attr('font-weight', '700')
          .text(phase.items)
          .style('opacity', 0)
          .transition()
          .delay(i * 150 + 400)
          .duration(300)
          .style('opacity', 1)
      } else {
        // Count in node for pending
        g.append('text')
          .attr('x', x)
          .attr('y', y + 5)
          .attr('text-anchor', 'middle')
          .attr('fill', '#787c99')
          .attr('font-size', '11px')
          .text(phase.items || '—')
          .style('opacity', 0)
          .transition()
          .delay(i * 150 + 400)
          .duration(300)
          .style('opacity', 1)
      }

      // Phase label
      g.append('text')
        .attr('x', x)
        .attr('y', y + 35)
        .attr('text-anchor', 'middle')
        .attr('fill', phase.status === 'pending' ? '#565f89' : '#c0caf5')
        .attr('font-size', '11px')
        .attr('font-weight', phase.status === 'active' ? '600' : '400')
        .text(phase.label)
        .style('opacity', 0)
        .transition()
        .delay(i * 150 + 200)
        .duration(300)
        .style('opacity', 1)
    })

  }, [metrics])

  // Render impact projection chart
  useEffect(() => {
    if (!impactRef.current) return

    const svg = d3.select(impactRef.current)
    svg.selectAll('*').remove()

    const width = 280
    const height = 140
    const margin = { top: 20, right: 20, bottom: 30, left: 20 }

    svg.attr('width', width).attr('height', height)

    const data = [
      { label: 'Complexity', before: metrics.avgComplexity || 20, after: Math.max(5, (metrics.avgComplexity || 20) - 10) },
      { label: 'High Risk', before: metrics.highRisk, after: 0 },
      { label: 'Actions', before: metrics.totalActions, after: 0 },
    ]

    const barHeight = 24
    const maxVal = d3.max(data, d => Math.max(d.before, 1))

    const x = d3.scaleLinear()
      .domain([0, maxVal])
      .range([margin.left, width - margin.right])

    const g = svg.append('g')

    data.forEach((d, i) => {
      const y = margin.top + i * (barHeight + 20)

      // Label
      g.append('text')
        .attr('x', margin.left)
        .attr('y', y - 4)
        .attr('fill', '#a9b1d6')
        .attr('font-size', '11px')
        .text(d.label)

      // Before bar (red/orange)
      g.append('rect')
        .attr('x', x(0))
        .attr('y', y)
        .attr('height', barHeight / 2 - 2)
        .attr('width', 0)
        .attr('fill', '#f7768e')
        .attr('rx', 2)
        .transition()
        .duration(800)
        .delay(i * 100)
        .attr('width', x(d.before) - x(0))

      // After bar (green) - projected
      g.append('rect')
        .attr('x', x(0))
        .attr('y', y + barHeight / 2)
        .attr('height', barHeight / 2 - 2)
        .attr('width', 0)
        .attr('fill', '#9ece6a')
        .attr('rx', 2)
        .transition()
        .duration(800)
        .delay(i * 100 + 400)
        .attr('width', x(d.after) - x(0))

      // Before value
      g.append('text')
        .attr('x', x(d.before) + 4)
        .attr('y', y + barHeight / 4)
        .attr('dy', '0.35em')
        .attr('fill', '#f7768e')
        .attr('font-size', '10px')
        .attr('font-weight', '600')
        .text(`${d.before} now`)
        .style('opacity', 0)
        .transition()
        .delay(i * 100 + 600)
        .duration(300)
        .style('opacity', 1)

      // After value
      g.append('text')
        .attr('x', x(d.after) + 4)
        .attr('y', y + barHeight * 0.75)
        .attr('dy', '0.35em')
        .attr('fill', '#9ece6a')
        .attr('font-size', '10px')
        .attr('font-weight', '600')
        .text(`${d.after} after`)
        .style('opacity', 0)
        .transition()
        .delay(i * 100 + 800)
        .duration(300)
        .style('opacity', 1)
    })

  }, [metrics])

  return (
    <div className="executive-insights-container">
      {/* Header */}
      <div className="insights-header">
        <h3 className="insights-title">
          <Brain size={24} />
          Transformation Intelligence
        </h3>
        <p className="insights-subtitle">
          AI-powered analysis of your codebase modernization potential
        </p>
      </div>

      {/* Main Content Grid */}
      <div className="insights-grid">
        {/* Readiness Score Card */}
        <div className="insights-card readiness-card">
          <div className="card-header">
            <Gauge size={18} />
            <h4>Modernization Readiness</h4>
          </div>
          <div className="gauge-container">
            <svg ref={gaugeRef}></svg>
          </div>
          <div className="readiness-factors">
            <div className="factor">
              <span className="factor-label">Confidence</span>
              <span className="factor-value" style={{ color: metrics.avgConfidence > 0.7 ? '#9ece6a' : '#e0af68' }}>
                {(metrics.avgConfidence * 100).toFixed(0)}%
              </span>
            </div>
            <div className="factor">
              <span className="factor-label">Low Risk Ratio</span>
              <span className="factor-value" style={{ color: metrics.lowRisk > metrics.highRisk ? '#9ece6a' : '#f7768e' }}>
                {metrics.totalRecipes > 0 ? ((metrics.lowRisk / metrics.totalRecipes) * 100).toFixed(0) : 0}%
              </span>
            </div>
            <div className="factor">
              <span className="factor-label">Complexity</span>
              <span className="factor-value" style={{ color: metrics.avgComplexity < 15 ? '#9ece6a' : metrics.avgComplexity < 25 ? '#e0af68' : '#f7768e' }}>
                {metrics.avgComplexity.toFixed(1)}
              </span>
            </div>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div className="insights-card metrics-card">
          <div className="card-header">
            <Activity size={18} />
            <h4>Transformation Summary</h4>
          </div>
          <div className="key-metrics">
            <div className="metric-item">
              <div className="metric-icon" style={{ background: '#7aa2f720', color: '#7aa2f7' }}>
                <Sparkles size={18} />
              </div>
              <div className="metric-details">
                <span className="metric-number">{metrics.totalRecipes}</span>
                <span className="metric-label">Recipes Generated</span>
              </div>
            </div>
            <div className="metric-item">
              <div className="metric-icon" style={{ background: '#f7768e20', color: '#f7768e' }}>
                <AlertTriangle size={18} />
              </div>
              <div className="metric-details">
                <span className="metric-number">{metrics.criticalActions}</span>
                <span className="metric-label">Critical Actions</span>
              </div>
            </div>
            <div className="metric-item">
              <div className="metric-icon" style={{ background: '#e0af6820', color: '#e0af68' }}>
                <Code2 size={18} />
              </div>
              <div className="metric-details">
                <span className="metric-number">{metrics.complexityPatterns}</span>
                <span className="metric-label">Complex Methods</span>
              </div>
            </div>
            <div className="metric-item">
              <div className="metric-icon" style={{ background: '#9ece6a20', color: '#9ece6a' }}>
                <Shield size={18} />
              </div>
              <div className="metric-details">
                <span className="metric-number">{metrics.lowRisk}</span>
                <span className="metric-label">Safe Changes</span>
              </div>
            </div>
          </div>
        </div>

        {/* Transformation Roadmap */}
        <div className="insights-card roadmap-card">
          <div className="card-header">
            <Rocket size={18} />
            <h4>Transformation Roadmap</h4>
          </div>
          <div className="roadmap-container">
            <svg ref={roadmapRef}></svg>
          </div>
          <div className="roadmap-legend">
            <span className="legend-item"><span className="dot complete"></span> Complete</span>
            <span className="legend-item"><span className="dot active"></span> In Progress</span>
            <span className="legend-item"><span className="dot pending"></span> Pending</span>
          </div>
        </div>

        {/* Impact Projections */}
        <div className="insights-card impact-card">
          <div className="card-header">
            <TrendingUp size={18} />
            <h4>Projected Impact</h4>
          </div>
          <div className="impact-container">
            <svg ref={impactRef}></svg>
          </div>
          <div className="impact-summary">
            <div className="impact-stat">
              <ArrowRight size={14} style={{ color: '#9ece6a' }} />
              <span>~{metrics.estimatedLinesReduced} lines reduced</span>
            </div>
            <div className="impact-stat">
              <ArrowRight size={14} style={{ color: '#9ece6a' }} />
              <span>~{metrics.estimatedTimeToModernize} days to modernize</span>
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div className="insights-card recommendations-card">
          <div className="card-header">
            <CheckCircle2 size={18} />
            <h4>Priority Recommendations</h4>
          </div>
          <div className="recommendations-list">
            {metrics.criticalActions > 0 && (
              <div className="recommendation high">
                <div className="rec-priority">P1</div>
                <div className="rec-content">
                  <span className="rec-title">Address {metrics.criticalActions} critical action{metrics.criticalActions !== 1 ? 's' : ''}</span>
                  <span className="rec-desc">High-priority items requiring immediate attention</span>
                </div>
              </div>
            )}
            {metrics.highRisk > 0 && (
              <div className="recommendation medium">
                <div className="rec-priority">P2</div>
                <div className="rec-content">
                  <span className="rec-title">Review {metrics.highRisk} high-risk recipe{metrics.highRisk !== 1 ? 's' : ''}</span>
                  <span className="rec-desc">Carefully review before applying these changes</span>
                </div>
              </div>
            )}
            {metrics.complexityPatterns > 0 && (
              <div className="recommendation medium">
                <div className="rec-priority">P2</div>
                <div className="rec-content">
                  <span className="rec-title">Refactor {metrics.complexityPatterns} complex method{metrics.complexityPatterns !== 1 ? 's' : ''}</span>
                  <span className="rec-desc">Methods exceeding complexity threshold</span>
                </div>
              </div>
            )}
            {metrics.lowRisk > 0 && (
              <div className="recommendation low">
                <div className="rec-priority">✓</div>
                <div className="rec-content">
                  <span className="rec-title">{metrics.lowRisk} safe change{metrics.lowRisk !== 1 ? 's' : ''} ready</span>
                  <span className="rec-desc">Low-risk recipes ready for implementation</span>
                </div>
              </div>
            )}
            {metrics.totalRecipes === 0 && metrics.totalActions === 0 && (
              <div className="recommendation low">
                <div className="rec-priority">✓</div>
                <div className="rec-content">
                  <span className="rec-title">Analysis pending</span>
                  <span className="rec-desc">Run refactor analysis to generate recommendations</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * 1. Recipe Summary - Multiple Chart Styles (Donut, Horizontal Bar, Radial Bar)
 */
function RecipeSummaryChart({ data, selectedType, onSelectType, showTooltip, hideTooltip, chartStyle, onStyleChange, availableStyles }) {
  const svgRef = useRef()
  const containerRef = useRef()

  useEffect(() => {
    const recipes = data?.recipes || []
    if (recipes.length === 0 || !svgRef.current) return

    // Compute breakdown from real recipe categories
    const breakdown = {}
    recipes.forEach(r => {
      const cat = r.category || 'unknown'
      breakdown[cat] = (breakdown[cat] || 0) + 1
    })
    const total = data.total_recipes || recipes.length

    const chartData = Object.entries(breakdown).map(([type, count]) => ({
      type,
      displayType: type.replace(/_/g, ' '),
      count,
    })).filter(d => d.count > 0).sort((a, b) => b.count - a.count)

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    // Get the actual SVG element's rendered size
    const svgEl = svgRef.current
    const bbox = svgEl.getBoundingClientRect()
    const width = Math.floor(bbox.width) || 600
    const height = Math.floor(bbox.height) || 400

    // Set SVG to fill container and use these dimensions
    svg.attr('width', width).attr('height', height)

    const color = d3.scaleOrdinal()
      .domain(chartData.map(d => d.type))
      .range(COLORS.chart)

    // Render based on selected style
    if (chartStyle === 'horizontal-bar') {
      renderHorizontalBar(svg, chartData, width, height, color, total, selectedType, onSelectType, showTooltip, hideTooltip)
    } else if (chartStyle === 'radial-bar') {
      renderRadialBar(svg, chartData, width, height, color, total, selectedType, onSelectType, showTooltip, hideTooltip)
    } else {
      renderDonut(svg, chartData, width, height, color, total, selectedType, onSelectType, showTooltip, hideTooltip)
    }

  }, [data, selectedType, onSelectType, showTooltip, hideTooltip, chartStyle])

  // Donut Chart Renderer
  function renderDonut(svg, chartData, width, height, color, total, selectedType, onSelectType, showTooltip, hideTooltip) {
    // Calculate legend dimensions - be generous with space
    const legendWidth = 180
    const legendPadding = 10

    // Donut uses left portion, legend uses right portion
    const donutCenterX = (width - legendWidth - legendPadding) / 2
    const donutCenterY = height / 2
    const maxRadius = Math.min(donutCenterX - 30, height / 2 - 30)
    const radius = Math.max(maxRadius, 80)

    const g = svg.append('g')
      .attr('transform', `translate(${donutCenterX},${donutCenterY})`)

    const pie = d3.pie().value(d => d.count).sort(null).padAngle(0.02)

    const arc = d3.arc().innerRadius(radius * 0.55).outerRadius(radius).cornerRadius(4)
    const hoverArc = d3.arc().innerRadius(radius * 0.55).outerRadius(radius + 10).cornerRadius(4)

    const arcs = g.selectAll('.arc')
      .data(pie(chartData))
      .enter()
      .append('g')
      .attr('class', 'arc')

    arcs.append('path')
      .attr('d', arc)
      .attr('fill', d => color(d.data.type))
      .attr('stroke', '#1a1b26')
      .attr('stroke-width', 2)
      .style('opacity', d => selectedType && selectedType !== d.data.type ? 0.3 : 0.9)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        if (!selectedType || selectedType === d.data.type) {
          d3.select(this).transition().duration(200).attr('d', hoverArc).style('opacity', 1)
        }
        showTooltip(
          <div className="tooltip-content">
            <div className="tooltip-title">{d.data.displayType}</div>
            <div className="tooltip-value">{d.data.count} recipes</div>
            <div className="tooltip-percent">{((d.data.count / total) * 100).toFixed(1)}%</div>
            <div className="tooltip-hint">Click to filter</div>
          </div>,
          event
        )
      })
      .on('mouseout', function(event, d) {
        d3.select(this).transition().duration(200).attr('d', arc)
          .style('opacity', selectedType && selectedType !== d.data.type ? 0.3 : 0.9)
        hideTooltip()
      })
      .on('click', (event, d) => onSelectType(selectedType === d.data.type ? null : d.data.type))
      .transition().duration(800)
      .attrTween('d', function(d) {
        const interpolate = d3.interpolate({ startAngle: 0, endAngle: 0 }, d)
        return t => arc(interpolate(t))
      })

    // Percentage labels
    g.selectAll('.label')
      .data(pie(chartData))
      .enter()
      .append('text')
      .attr('class', 'label')
      .attr('transform', d => `translate(${arc.centroid(d)})`)
      .attr('text-anchor', 'middle')
      .attr('fill', 'white')
      .attr('font-size', '13px')
      .attr('font-weight', '600')
      .style('pointer-events', 'none')
      .text(d => d.data.count > 0 ? `${((d.data.count / total) * 100).toFixed(0)}%` : '')
      .style('opacity', 0)
      .transition().delay(800).duration(300).style('opacity', 1)

    // Center text
    g.append('text').attr('text-anchor', 'middle').attr('dy', '-0.3em')
      .attr('fill', '#c0caf5').attr('font-size', '32px').attr('font-weight', '700').text(total)
    g.append('text').attr('text-anchor', 'middle').attr('dy', '1.3em')
      .attr('fill', '#787c99').attr('font-size', '13px').text('Total Recipes')

    // Legend - position at right edge of SVG, vertically centered
    const legendX = width - legendWidth - legendPadding
    const legendHeight = chartData.length * 26
    const legendY = Math.max(20, (height - legendHeight) / 2)
    const legend = svg.append('g').attr('class', 'legend').attr('transform', `translate(${legendX}, ${legendY})`)
    const legendItems = legend.selectAll('.legend-item')
      .data(chartData).enter().append('g').attr('class', 'legend-item')
      .attr('transform', (d, i) => `translate(0, ${i * 26})`)
      .style('cursor', 'pointer')
      .style('opacity', d => selectedType && selectedType !== d.type ? 0.4 : 1)
      .on('click', (event, d) => onSelectType(selectedType === d.type ? null : d.type))

    legendItems.append('rect').attr('width', 14).attr('height', 14).attr('rx', 3).attr('fill', d => color(d.type))
    legendItems.append('text').attr('x', 20).attr('y', 11).attr('fill', '#c0caf5').attr('font-size', '11px')
      .text(d => `${d.displayType} (${d.count})`)
  }

  // Horizontal Bar Chart Renderer
  function renderHorizontalBar(svg, chartData, width, height, color, total, selectedType, onSelectType, showTooltip, hideTooltip) {
    const margin = { top: 30, right: 100, bottom: 30, left: 140 }
    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

    const x = d3.scaleLinear().domain([0, d3.max(chartData, d => d.count)]).range([0, innerWidth])
    const y = d3.scaleBand().domain(chartData.map(d => d.displayType)).range([0, innerHeight]).padding(0.3)

    // Bars with gradient
    const defs = svg.append('defs')
    chartData.forEach((d, i) => {
      const gradient = defs.append('linearGradient')
        .attr('id', `bar-gradient-${i}`)
        .attr('x1', '0%').attr('x2', '100%')
      gradient.append('stop').attr('offset', '0%').attr('stop-color', color(d.type))
      gradient.append('stop').attr('offset', '100%').attr('stop-color', d3.color(color(d.type)).brighter(0.5))
    })

    g.selectAll('.bar')
      .data(chartData)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('y', d => y(d.displayType))
      .attr('height', y.bandwidth())
      .attr('x', 0)
      .attr('width', 0)
      .attr('fill', (d, i) => `url(#bar-gradient-${i})`)
      .attr('rx', 4)
      .style('opacity', d => selectedType && selectedType !== d.type ? 0.3 : 1)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this).transition().duration(150)
          .style('opacity', 1)
          .attr('transform', 'translate(4, 0)')
        showTooltip(
          <div className="tooltip-content">
            <div className="tooltip-title">{d.displayType}</div>
            <div className="tooltip-value">{d.count} recipes</div>
            <div className="tooltip-percent">{((d.count / total) * 100).toFixed(1)}%</div>
          </div>,
          event
        )
      })
      .on('mouseout', function(event, d) {
        d3.select(this).transition().duration(150)
          .style('opacity', selectedType && selectedType !== d.type ? 0.3 : 1)
          .attr('transform', 'translate(0, 0)')
        hideTooltip()
      })
      .on('click', (event, d) => onSelectType(selectedType === d.type ? null : d.type))
      .transition().duration(800).delay((d, i) => i * 80)
      .attr('width', d => x(d.count))

    // Value labels
    g.selectAll('.value-label')
      .data(chartData)
      .enter()
      .append('text')
      .attr('class', 'value-label')
      .attr('x', d => x(d.count) + 8)
      .attr('y', d => y(d.displayType) + y.bandwidth() / 2)
      .attr('dy', '0.35em')
      .attr('fill', '#c0caf5')
      .attr('font-size', '12px')
      .attr('font-weight', '600')
      .text(d => `${d.count} (${((d.count / total) * 100).toFixed(0)}%)`)
      .style('opacity', 0)
      .transition().delay(800).duration(300).style('opacity', 1)

    // Y axis labels
    g.append('g')
      .call(d3.axisLeft(y).tickSize(0))
      .selectAll('text')
      .attr('fill', '#c0caf5')
      .attr('font-size', '12px')
      .style('cursor', 'pointer')
      .on('click', function(event, label) {
        const item = chartData.find(d => d.displayType === label)
        if (item) onSelectType(selectedType === item.type ? null : item.type)
      })

    g.select('.domain').remove()
  }

  // Radial Bar Chart Renderer - uses full width with integrated labels
  function renderRadialBar(svg, chartData, width, height, color, total, selectedType, onSelectType, showTooltip, hideTooltip) {
    const cx = width / 2
    const cy = height / 2
    const maxRadius = Math.min(width, height) / 2 - 30
    const minRadius = maxRadius * 0.25
    const barHeight = Math.max(8, (maxRadius - minRadius) / chartData.length - 3)

    const g = svg.append('g').attr('transform', `translate(${cx},${cy})`)

    const maxValue = d3.max(chartData, d => d.count)
    const angleScale = d3.scaleLinear().domain([0, maxValue]).range([0, 2 * Math.PI * 0.75])

    chartData.forEach((d, i) => {
      const innerR = minRadius + i * (barHeight + 4)
      const outerR = innerR + barHeight
      const endAngle = angleScale(d.count)

      const arc = d3.arc()
        .innerRadius(innerR)
        .outerRadius(outerR)
        .startAngle(-Math.PI / 2)
        .endAngle(-Math.PI / 2 + endAngle)
        .cornerRadius(4)

      // Background track
      g.append('path')
        .attr('d', d3.arc()
          .innerRadius(innerR)
          .outerRadius(outerR)
          .startAngle(-Math.PI / 2)
          .endAngle(Math.PI * 0.75 + Math.PI / 2)())
        .attr('fill', '#2a2b3d')
        .attr('opacity', 0.5)

      // Animated bar
      g.append('path')
        .attr('class', 'radial-bar')
        .attr('fill', color(d.type))
        .style('opacity', selectedType && selectedType !== d.type ? 0.3 : 1)
        .style('cursor', 'pointer')
        .on('mouseover', function(event) {
          d3.select(this).transition().duration(150).style('opacity', 1)
          showTooltip(
            <div className="tooltip-content">
              <div className="tooltip-title">{d.displayType}</div>
              <div className="tooltip-value">{d.count} recipes</div>
              <div className="tooltip-percent">{((d.count / total) * 100).toFixed(1)}%</div>
            </div>,
            event
          )
        })
        .on('mouseout', function() {
          d3.select(this).transition().duration(150)
            .style('opacity', selectedType && selectedType !== d.type ? 0.3 : 1)
          hideTooltip()
        })
        .on('click', () => onSelectType(selectedType === d.type ? null : d.type))
        .transition().duration(800).delay(i * 100)
        .attrTween('d', function() {
          const interp = d3.interpolate(0, endAngle)
          return t => d3.arc()
            .innerRadius(innerR)
            .outerRadius(outerR)
            .startAngle(-Math.PI / 2)
            .endAngle(-Math.PI / 2 + interp(t))
            .cornerRadius(4)()
        })

      // Label
      g.append('text')
        .attr('x', -minRadius + 20)
        .attr('y', -(innerR + barHeight / 2))
        .attr('text-anchor', 'end')
        .attr('fill', '#c0caf5')
        .attr('font-size', '11px')
        .attr('dominant-baseline', 'middle')
        .text(d.displayType.length > 15 ? d.displayType.substring(0, 15) + '...' : d.displayType)
    })

    // Center stats
    g.append('text').attr('text-anchor', 'middle').attr('dy', '-0.3em')
      .attr('fill', '#c0caf5').attr('font-size', '28px').attr('font-weight', '700').text(total)
    g.append('text').attr('text-anchor', 'middle').attr('dy', '1.3em')
      .attr('fill', '#787c99').attr('font-size', '12px').text('Recipes')
  }

  return (
    <div className="chart-wrapper" ref={containerRef}>
      <div className="chart-header">
        <div className="chart-header-left">
          <h3 className="chart-title">Recipe Type Distribution</h3>
          <p className="chart-description">Click segments to filter by recipe type</p>
        </div>
      </div>
      <svg ref={svgRef}></svg>
    </div>
  )
}

/**
 * 2. Method Complexity - Multiple Styles (Lollipop, Horizontal Bar, Diverging)
 */
function MethodComplexityChart({ data, recipesData, selectedClass, showTooltip, hideTooltip, chartStyle, onStyleChange, availableStyles }) {
  const svgRef = useRef()
  const containerRef = useRef()
  const [chartEmpty, setChartEmpty] = useState(false)

  const getBarColor = (complexity) => {
    if (complexity > 25) return '#f7768e'
    if (complexity > 15) return '#e0af68'
    return '#7aa2f7'
  }

  useEffect(() => {
    if (!svgRef.current) return

    const complexityPatterns = data?.patterns?.filter(p => p.pattern_type === 'high_complexity_method') || []

    let chartData = complexityPatterns.map(p => ({
      method: p.location?.method?.replace(/_/g, ' ') || 'Unknown',
      fullMethod: p.location?.method || 'Unknown',
      className: p.class_name || 'Unknown',
      complexity: p.evidence?.complexity || 0,
      threshold: p.evidence?.threshold || 10,
      lines: p.evidence?.lines || 0,
    })).sort((a, b) => b.complexity - a.complexity).slice(0, 15)

    // Fallback: derive method impact from recipe steps when no pattern data exists
    if (chartData.length === 0 && recipesData?.recipes?.length > 0) {
      const methodMap = {}
      recipesData.recipes.forEach(r => {
        const priorityScore = r.priority === 'high' ? 10 : r.priority === 'medium' ? 6 : 3
        r.steps?.forEach(s => {
          const methods = [...(s.methods_to_move || []), ...(s.methods_to_modify || [])]
          const fieldCount = (s.fields_to_move || []).length
          methods.forEach(m => {
            if (!methodMap[m]) methodMap[m] = { method: m, fullMethod: m, className: r.name, complexity: 0, lines: fieldCount, threshold: 10 }
            methodMap[m].complexity += priorityScore + Math.floor(fieldCount / 2)
          })
        })
      })
      chartData = Object.values(methodMap).sort((a, b) => b.complexity - a.complexity)
    }

    if (selectedClass) {
      chartData = chartData.filter(d => d.className === selectedClass)
    }

    const container = containerRef.current
    const width = container.clientWidth
    const height = container.clientHeight

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    svg.attr('width', width).attr('height', height)

    if (chartData.length === 0) {
      svg.selectAll('*').remove()
      setChartEmpty(true)
      return
    }

    setChartEmpty(false)

    if (chartStyle === 'horizontal-bar') {
      renderHorizontalBar(svg, chartData, width, height, showTooltip, hideTooltip)
    } else if (chartStyle === 'diverging') {
      renderDivergingBar(svg, chartData, width, height, showTooltip, hideTooltip)
    } else {
      renderLollipop(svg, chartData, width, height, showTooltip, hideTooltip)
    }
  }, [data, recipesData, selectedClass, showTooltip, hideTooltip, chartStyle])

  // Lollipop Chart
  function renderLollipop(svg, chartData, width, height, showTooltip, hideTooltip) {
    const margin = { top: 40, right: 40, bottom: 60, left: 180 }

    const y = d3.scaleBand()
      .domain(chartData.map(d => d.method))
      .range([margin.top, height - margin.bottom])
      .padding(0.4)

    const x = d3.scaleLinear()
      .domain([0, d3.max(chartData, d => d.complexity) * 1.15])
      .range([margin.left, width - margin.right])

    const g = svg.append('g')

    // Horizontal lines
    g.selectAll('.line')
      .data(chartData)
      .enter()
      .append('line')
      .attr('class', 'line')
      .attr('x1', margin.left)
      .attr('x2', margin.left)
      .attr('y1', d => y(d.method) + y.bandwidth() / 2)
      .attr('y2', d => y(d.method) + y.bandwidth() / 2)
      .attr('stroke', d => getBarColor(d.complexity))
      .attr('stroke-width', 3)
      .attr('stroke-linecap', 'round')
      .transition().duration(600).delay((d, i) => i * 50)
      .attr('x2', d => x(d.complexity))

    // Circles at the end
    g.selectAll('.dot')
      .data(chartData)
      .enter()
      .append('circle')
      .attr('class', 'dot')
      .attr('cx', margin.left)
      .attr('cy', d => y(d.method) + y.bandwidth() / 2)
      .attr('r', 0)
      .attr('fill', d => getBarColor(d.complexity))
      .attr('stroke', '#1a1b26')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this).transition().duration(150).attr('r', 12)
        showTooltip(
          <div className="tooltip-content">
            <div className="tooltip-title">{d.fullMethod}</div>
            <div className="tooltip-row"><span>Class:</span><span>{d.className}</span></div>
            <div className="tooltip-row"><span>Complexity:</span><span className={d.complexity > 20 ? 'danger' : d.complexity > 15 ? 'warning' : ''}>{d.complexity}</span></div>
            <div className="tooltip-row"><span>Lines:</span><span>{d.lines}</span></div>
          </div>,
          event
        )
      })
      .on('mouseout', function() {
        d3.select(this).transition().duration(150).attr('r', 8)
        hideTooltip()
      })
      .transition().duration(600).delay((d, i) => i * 50)
      .attr('cx', d => x(d.complexity))
      .attr('r', 8)

    // Value labels
    g.selectAll('.value')
      .data(chartData)
      .enter()
      .append('text')
      .attr('class', 'value')
      .attr('x', d => x(d.complexity) + 16)
      .attr('y', d => y(d.method) + y.bandwidth() / 2)
      .attr('dy', '0.35em')
      .attr('fill', '#c0caf5')
      .attr('font-size', '11px')
      .attr('font-weight', '600')
      .text(d => d.complexity)
      .style('opacity', 0)
      .transition().delay(600).duration(300).style('opacity', 1)

    // Threshold line
    const thresholdX = x(10)
    g.append('line')
      .attr('x1', thresholdX).attr('x2', thresholdX)
      .attr('y1', margin.top).attr('y2', height - margin.bottom)
      .attr('stroke', '#f7768e').attr('stroke-dasharray', '6,4').attr('stroke-width', 2)
      .style('opacity', 0).transition().delay(600).duration(300).style('opacity', 0.7)

    g.append('text')
      .attr('x', thresholdX + 5).attr('y', margin.top - 8)
      .attr('fill', '#f7768e').attr('font-size', '10px')
      .text('Threshold')

    // Y axis
    svg.append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).tickSize(0))
      .selectAll('text').attr('fill', '#c0caf5').attr('font-size', '11px')
    svg.select('.domain').remove()

    // X axis
    svg.append('g')
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x).ticks(6))
      .selectAll('text').attr('fill', '#787c99')
  }

  // Horizontal Bar Chart
  function renderHorizontalBar(svg, chartData, width, height, showTooltip, hideTooltip) {
    const margin = { top: 40, right: 80, bottom: 40, left: 180 }

    const y = d3.scaleBand()
      .domain(chartData.map(d => d.method))
      .range([margin.top, height - margin.bottom])
      .padding(0.25)

    const x = d3.scaleLinear()
      .domain([0, d3.max(chartData, d => d.complexity) * 1.1])
      .range([margin.left, width - margin.right])

    // Create gradients
    const defs = svg.append('defs')
    chartData.forEach((d, i) => {
      const gradient = defs.append('linearGradient')
        .attr('id', `complexity-gradient-${i}`)
        .attr('x1', '0%').attr('x2', '100%')
      gradient.append('stop').attr('offset', '0%').attr('stop-color', getBarColor(d.complexity))
      gradient.append('stop').attr('offset', '100%').attr('stop-color', d3.color(getBarColor(d.complexity)).brighter(0.4))
    })

    svg.selectAll('.bar')
      .data(chartData)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', margin.left)
      .attr('y', d => y(d.method))
      .attr('height', y.bandwidth())
      .attr('width', 0)
      .attr('fill', (d, i) => `url(#complexity-gradient-${i})`)
      .attr('rx', 4)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this).transition().duration(150).attr('opacity', 0.8)
        showTooltip(
          <div className="tooltip-content">
            <div className="tooltip-title">{d.fullMethod}</div>
            <div className="tooltip-row"><span>Complexity:</span><span>{d.complexity}</span></div>
            <div className="tooltip-row"><span>Lines:</span><span>{d.lines}</span></div>
          </div>,
          event
        )
      })
      .on('mouseout', function() {
        d3.select(this).transition().duration(150).attr('opacity', 1)
        hideTooltip()
      })
      .transition().duration(600).delay((d, i) => i * 50)
      .attr('width', d => x(d.complexity) - margin.left)

    // Value labels
    svg.selectAll('.value')
      .data(chartData)
      .enter()
      .append('text')
      .attr('x', d => x(d.complexity) + 8)
      .attr('y', d => y(d.method) + y.bandwidth() / 2)
      .attr('dy', '0.35em')
      .attr('fill', '#c0caf5')
      .attr('font-size', '12px')
      .attr('font-weight', '600')
      .text(d => d.complexity)
      .style('opacity', 0)
      .transition().delay(600).duration(300).style('opacity', 1)

    // Y axis
    svg.append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).tickSize(0))
      .selectAll('text').attr('fill', '#c0caf5').attr('font-size', '11px')
    svg.select('.domain').remove()
  }

  // Diverging Bar Chart (shows deviation from threshold)
  function renderDivergingBar(svg, chartData, width, height, showTooltip, hideTooltip) {
    const margin = { top: 40, right: 60, bottom: 40, left: 180 }
    const threshold = 10

    const y = d3.scaleBand()
      .domain(chartData.map(d => d.method))
      .range([margin.top, height - margin.bottom])
      .padding(0.3)

    const maxDeviation = d3.max(chartData, d => Math.abs(d.complexity - threshold))
    const x = d3.scaleLinear()
      .domain([-maxDeviation * 0.3, maxDeviation])
      .range([margin.left, width - margin.right])

    const centerX = x(0)

    // Center line
    svg.append('line')
      .attr('x1', centerX).attr('x2', centerX)
      .attr('y1', margin.top - 10).attr('y2', height - margin.bottom + 10)
      .attr('stroke', '#565f89').attr('stroke-width', 2)

    svg.append('text')
      .attr('x', centerX).attr('y', margin.top - 15)
      .attr('text-anchor', 'middle').attr('fill', '#787c99').attr('font-size', '10px')
      .text(`Threshold (${threshold})`)

    // Bars
    svg.selectAll('.bar')
      .data(chartData)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', centerX)
      .attr('y', d => y(d.method))
      .attr('height', y.bandwidth())
      .attr('width', 0)
      .attr('fill', d => d.complexity > threshold ? '#f7768e' : '#9ece6a')
      .attr('rx', 3)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this).transition().duration(150).attr('opacity', 0.8)
        showTooltip(
          <div className="tooltip-content">
            <div className="tooltip-title">{d.fullMethod}</div>
            <div className="tooltip-row"><span>Complexity:</span><span>{d.complexity}</span></div>
            <div className="tooltip-row"><span>Deviation:</span><span>{d.complexity > threshold ? '+' : ''}{d.complexity - threshold}</span></div>
          </div>,
          event
        )
      })
      .on('mouseout', function() {
        d3.select(this).transition().duration(150).attr('opacity', 1)
        hideTooltip()
      })
      .transition().duration(600).delay((d, i) => i * 50)
      .attr('x', d => d.complexity > threshold ? centerX : x(d.complexity - threshold))
      .attr('width', d => Math.abs(x(d.complexity - threshold) - centerX))

    // Value labels
    svg.selectAll('.value')
      .data(chartData)
      .enter()
      .append('text')
      .attr('x', d => d.complexity > threshold ? x(d.complexity - threshold) + 8 : x(d.complexity - threshold) - 8)
      .attr('y', d => y(d.method) + y.bandwidth() / 2)
      .attr('dy', '0.35em')
      .attr('text-anchor', d => d.complexity > threshold ? 'start' : 'end')
      .attr('fill', '#c0caf5')
      .attr('font-size', '11px')
      .attr('font-weight', '600')
      .text(d => d.complexity)
      .style('opacity', 0)
      .transition().delay(600).duration(300).style('opacity', 1)

    // Y axis
    svg.append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).tickSize(0))
      .selectAll('text').attr('fill', '#c0caf5').attr('font-size', '11px')
    svg.select('.domain').remove()
  }

  return (
    <div className="chart-wrapper" ref={containerRef}>
      <div className="chart-header">
        <div className="chart-header-left">
          <h3 className="chart-title">Method Impact Analysis</h3>
          <p className="chart-description">Methods involved in refactoring — sized by priority impact</p>
        </div>
      </div>
      {chartEmpty ? (
        <div className="chart-empty-state">
          <p>No high-complexity methods detected for this run.</p>
        </div>
      ) : (
        <svg ref={svgRef}></svg>
      )}
    </div>
  )
}

/**
 * 3. Method Grouping - Multiple Styles (Sunburst, Circle Packing, Treemap)
 */
function MethodGroupingChart({ data, showTooltip, hideTooltip, chartStyle, onStyleChange, availableStyles }) {
  const svgRef = useRef()
  const containerRef = useRef()
  const [zoomedNode, setZoomedNode] = useState(null)
  const [chartEmpty, setChartEmpty] = useState(false)

  useEffect(() => {
    if (!data?.patterns || !svgRef.current) return

    const groupings = data.patterns.filter(
      p => p.pattern_type === 'method_grouping' || p.pattern_type === 'field_grouping'
    )

    if (groupings.length === 0) {
      d3.select(svgRef.current).selectAll('*').remove()
      setChartEmpty(true)
      return
    }

    setChartEmpty(false)

    const hierarchyData = {
      name: 'All Groupings',
      children: groupings.map(g => ({
        name: `${g.evidence?.prefix || 'Unknown'}-group`,
        prefix: g.evidence?.prefix,
        count: g.evidence?.field_count || g.evidence?.method_count || 0,
        className: g.class_name,
        children: (g.evidence?.field_names || g.evidence?.method_names || []).slice(0, 15).map(m => ({
          name: m,
          value: 1,
        })),
      })),
    }

    const container = containerRef.current
    const width = container.clientWidth
    const height = container.clientHeight
    const radius = Math.min(width, height) / 2 - 30

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${width / 2},${height / 2})`)

    const colors = ['#7aa2f7', '#9ece6a', '#e0af68', '#f7768e', '#bb9af7', '#7dcfff', '#ff9e64', '#73daca']
    const color = d3.scaleOrdinal()
      .domain(hierarchyData.children.map(d => d.name))
      .range(colors)

    // Render based on chart style
    if (chartStyle === 'circle-pack') {
      renderCirclePack(svg, hierarchyData, width, height, color, showTooltip, hideTooltip)
    } else if (chartStyle === 'treemap') {
      renderTreemapGrouping(svg, hierarchyData, width, height, color, showTooltip, hideTooltip)
    } else {
      renderSunburst(svg, g, hierarchyData, width, height, radius, color, groupings, showTooltip, hideTooltip, setZoomedNode, zoomedNode)
    }

    function renderSunburst(svg, g, hierarchyData, width, height, radius, color, groupings, showTooltip, hideTooltip, setZoomedNode, zoomedNode) {
      const partition = d3.partition()
        .size([2 * Math.PI, radius])

      const root = d3.hierarchy(hierarchyData)
        .sum(d => d.value || d.count || 1)

      partition(root)

      const arc = d3.arc()
        .startAngle(d => d.x0)
        .endAngle(d => d.x1)
        .padAngle(0.01)
        .padRadius(radius / 2)
        .innerRadius(d => d.y0)
        .outerRadius(d => d.y1 - 2)

      // Draw arcs
      g.selectAll('path')
        .data(root.descendants().filter(d => d.depth))
        .enter()
        .append('path')
        .attr('d', arc)
        .attr('fill', d => {
          let node = d
          while (node.depth > 1) node = node.parent
          return color(node.data.name)
        })
        .attr('fill-opacity', d => d.depth === 1 ? 0.9 : 0.6)
        .attr('stroke', '#1a1b26')
        .attr('stroke-width', 1)
        .style('cursor', 'pointer')
        .on('mouseover', function(event, d) {
          d3.select(this)
            .transition()
            .duration(150)
            .attr('fill-opacity', 1)
            .attr('stroke-width', 2)

          const parentName = d.depth > 1 ? d.parent.data.name : d.data.name
          showTooltip(
            <div className="tooltip-content">
              <div className="tooltip-title">{d.data.name}</div>
              {d.depth === 1 && (
                <>
                  <div className="tooltip-row">
                    <span>Methods:</span>
                    <span>{d.data.count}</span>
                  </div>
                  <div className="tooltip-row">
                    <span>Class:</span>
                    <span>{d.data.className}</span>
                  </div>
                </>
              )}
              {d.depth > 1 && (
                <div className="tooltip-row">
                  <span>Group:</span>
                  <span>{parentName}</span>
                </div>
              )}
              <div className="tooltip-hint">Click to zoom</div>
            </div>,
            event
          )
        })
        .on('mouseout', function(event, d) {
          d3.select(this)
            .transition()
            .duration(150)
            .attr('fill-opacity', d.depth === 1 ? 0.9 : 0.6)
            .attr('stroke-width', 1)
          hideTooltip()
        })
        .on('click', function(event, d) {
          setZoomedNode(zoomedNode === d ? null : d)
        })
        .transition()
        .duration(800)
        .attrTween('d', function(d) {
          const interpolate = d3.interpolate(
            { x0: 0, x1: 0, y0: d.y0, y1: d.y1 },
            d
          )
          return t => arc(interpolate(t))
        })

      // Center labels for main segments
      g.selectAll('.segment-label')
        .data(root.descendants().filter(d => d.depth === 1 && (d.x1 - d.x0) > 0.3))
        .enter()
        .append('text')
        .attr('class', 'segment-label')
        .attr('transform', d => {
          const angle = (d.x0 + d.x1) / 2 * 180 / Math.PI - 90
          const r = (d.y0 + d.y1) / 2
          return `rotate(${angle}) translate(${r},0) rotate(${angle > 90 ? 180 : 0})`
        })
        .attr('text-anchor', 'middle')
        .attr('dy', '0.35em')
        .attr('fill', 'white')
        .attr('font-size', '11px')
        .attr('font-weight', '600')
        .style('pointer-events', 'none')
        .text(d => d.data.name)
        .style('opacity', 0)
        .transition()
        .delay(800)
        .duration(300)
        .style('opacity', 1)

      // Center stats
      const totalMethods = groupings.reduce((sum, g) => sum + (g.evidence?.method_count || 0), 0)

      g.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '-0.2em')
        .attr('fill', '#c0caf5')
        .attr('font-size', '28px')
        .attr('font-weight', '700')
        .text(totalMethods)

      g.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '1.3em')
        .attr('fill', '#787c99')
        .attr('font-size', '12px')
        .text('Grouped Methods')
    }

    function renderCirclePack(svg, hierarchyData, width, height, color, showTooltip, hideTooltip) {
      const pack = d3.pack()
        .size([width - 4, height - 4])
        .padding(4)

      const root = d3.hierarchy(hierarchyData)
        .sum(d => d.value || d.count || 1)
        .sort((a, b) => b.value - a.value)

      pack(root)

      const g = svg.append('g').attr('transform', 'translate(2,2)')

      // Draw circles
      const nodes = g.selectAll('circle')
        .data(root.descendants())
        .enter()
        .append('circle')
        .attr('cx', d => d.x)
        .attr('cy', d => d.y)
        .attr('r', 0)
        .attr('fill', d => {
          if (d.depth === 0) return 'transparent'
          if (d.depth === 1) return color(d.data.name)
          let node = d
          while (node.depth > 1) node = node.parent
          return d3.color(color(node.data.name)).brighter(0.3)
        })
        .attr('fill-opacity', d => d.depth === 0 ? 0 : d.depth === 1 ? 0.3 : 0.8)
        .attr('stroke', d => d.depth === 1 ? color(d.data.name) : 'none')
        .attr('stroke-width', d => d.depth === 1 ? 2 : 0)
        .style('cursor', d => d.depth > 0 ? 'pointer' : 'default')
        .on('mouseover', function(event, d) {
          if (d.depth === 0) return
          d3.select(this).transition().duration(150)
            .attr('fill-opacity', d.depth === 1 ? 0.5 : 1)
            .attr('stroke-width', d.depth === 1 ? 3 : 1)
          showTooltip(
            <div className="tooltip-content">
              <div className="tooltip-title">{d.data.name}</div>
              {d.depth === 1 && (
                <div className="tooltip-row"><span>Methods:</span><span>{d.data.count || d.children?.length}</span></div>
              )}
              {d.data.className && (
                <div className="tooltip-row"><span>Class:</span><span>{d.data.className}</span></div>
              )}
            </div>,
            event
          )
        })
        .on('mouseout', function(event, d) {
          d3.select(this).transition().duration(150)
            .attr('fill-opacity', d.depth === 0 ? 0 : d.depth === 1 ? 0.3 : 0.8)
            .attr('stroke-width', d.depth === 1 ? 2 : 0)
          hideTooltip()
        })

      nodes.transition().duration(800).delay((d, i) => i * 10)
        .attr('r', d => d.r)

      // Labels for group circles
      g.selectAll('.pack-label')
        .data(root.descendants().filter(d => d.depth === 1 && d.r > 30))
        .enter()
        .append('text')
        .attr('class', 'pack-label')
        .attr('x', d => d.x)
        .attr('y', d => d.y)
        .attr('text-anchor', 'middle')
        .attr('dy', '0.35em')
        .attr('fill', '#c0caf5')
        .attr('font-size', '11px')
        .attr('font-weight', '600')
        .style('pointer-events', 'none')
        .text(d => d.data.name.length > 12 ? d.data.name.substring(0, 10) + '...' : d.data.name)
        .style('opacity', 0)
        .transition().delay(800).duration(300).style('opacity', 1)
    }

    function renderTreemapGrouping(svg, hierarchyData, width, height, color, showTooltip, hideTooltip) {
      const margin = { top: 10, right: 10, bottom: 10, left: 10 }
      const treemap = d3.treemap()
        .size([width - margin.left - margin.right, height - margin.top - margin.bottom])
        .paddingOuter(4)
        .paddingTop(22)
        .paddingInner(2)
        .round(true)

      const root = d3.hierarchy(hierarchyData)
        .sum(d => d.value || d.count || 1)
        .sort((a, b) => b.value - a.value)

      treemap(root)

      const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

      // Group containers
      const groups = g.selectAll('.group')
        .data(root.children || [])
        .enter()
        .append('g')
        .attr('class', 'group')

      groups.append('rect')
        .attr('x', d => d.x0)
        .attr('y', d => d.y0)
        .attr('width', d => d.x1 - d.x0)
        .attr('height', d => d.y1 - d.y0)
        .attr('fill', d => color(d.data.name))
        .attr('fill-opacity', 0.15)
        .attr('stroke', d => color(d.data.name))
        .attr('stroke-width', 2)
        .attr('rx', 6)

      groups.append('text')
        .attr('x', d => d.x0 + 6)
        .attr('y', d => d.y0 + 15)
        .attr('fill', d => color(d.data.name))
        .attr('font-size', '11px')
        .attr('font-weight', '700')
        .text(d => `${d.data.name} (${d.data.count || d.children?.length || 0})`)

      // Leaf rectangles
      const leaves = g.selectAll('.leaf')
        .data(root.leaves())
        .enter()
        .append('g')
        .attr('class', 'leaf')
        .attr('transform', d => `translate(${d.x0},${d.y0})`)
        .style('cursor', 'pointer')
        .on('mouseover', function(event, d) {
          d3.select(this).select('rect').transition().duration(150)
            .attr('fill-opacity', 1).attr('stroke-width', 2)
          let parentNode = d.parent
          showTooltip(
            <div className="tooltip-content">
              <div className="tooltip-title">{d.data.name}</div>
              <div className="tooltip-row"><span>Group:</span><span>{parentNode?.data?.name}</span></div>
            </div>,
            event
          )
        })
        .on('mouseout', function() {
          d3.select(this).select('rect').transition().duration(150)
            .attr('fill-opacity', 0.8).attr('stroke-width', 0)
          hideTooltip()
        })

      leaves.append('rect')
        .attr('width', d => Math.max(0, d.x1 - d.x0))
        .attr('height', d => Math.max(0, d.y1 - d.y0))
        .attr('fill', d => {
          let node = d
          while (node.depth > 1) node = node.parent
          return color(node.data.name)
        })
        .attr('fill-opacity', 0.8)
        .attr('stroke', '#1a1b26')
        .attr('rx', 3)

      leaves.append('text')
        .attr('x', 4)
        .attr('y', 14)
        .attr('fill', 'white')
        .attr('font-size', '10px')
        .each(function(d) {
          const availableWidth = d.x1 - d.x0 - 8
          const text = d.data.name
          const self = d3.select(this)
          if (availableWidth < 30) self.text('')
          else if (text.length * 5 > availableWidth) self.text(text.substring(0, Math.floor(availableWidth / 5) - 1) + '…')
          else self.text(text)
        })
    }

  }, [data, zoomedNode, showTooltip, hideTooltip, chartStyle])

  return (
    <div className="chart-wrapper" ref={containerRef}>
      <div className="chart-header">
        <div className="chart-header-left">
          <h3 className="chart-title">COBOL Field & Method Groupings</h3>
          <p className="chart-description">Fields and methods grouped by naming convention prefix</p>
        </div>
      </div>
      {chartEmpty ? (
        <div className="chart-empty-state">
          <p>No method groupings were generated for this run.</p>
        </div>
      ) : (
        <svg ref={svgRef}></svg>
      )}
    </div>
  )
}

/**
 * 4. Confidence/Risk Matrix - Multiple Styles (Bubble, Scatter, Heatmap)
 */
function ConfidenceRiskChart({ data, selectedType, onSelectType, showTooltip, hideTooltip, chartStyle, onStyleChange, availableStyles }) {
  const svgRef = useRef()
  const containerRef = useRef()

  useEffect(() => {
    if (!data?.recipes || !svgRef.current) return

    const riskLevels = { 'low': 1, 'medium': 2, 'high': 3 }
    let chartData = data.recipes.map(r => ({
      id: r.recipe_id,
      type: r.category,
      displayType: r.category?.replace(/_/g, ' ') || 'Unknown',
      confidence: r.confidence || 0.5,
      risk: riskLevels[r.priority] || 2,
      riskLabel: r.priority || 'medium',
      title: r.name || r.category,
      description: r.description || '',
      rationale: r.rationale || '',
      risksList: r.risks || [],
      proposedClass: r.steps?.[0]?.new_class || null,
      source: r.source || null,
    }))

    if (selectedType) {
      chartData = chartData.filter(d => d.type === selectedType)
    }

    const container = containerRef.current
    const width = container.clientWidth
    const height = container.clientHeight
    const margin = { top: 50, right: 50, bottom: 70, left: 70 }

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    svg.attr('width', width).attr('height', height)

    const x = d3.scaleLinear()
      .domain([0, 1])
      .range([margin.left, width - margin.right])

    const y = d3.scaleLinear()
      .domain([0.5, 3.5])
      .range([height - margin.bottom, margin.top])

    const types = [...new Set(chartData.map(d => d.type))]
    const color = d3.scaleOrdinal()
      .domain(types)
      .range(['#7aa2f7', '#9ece6a', '#e0af68', '#f7768e', '#bb9af7', '#7dcfff', '#ff9e64', '#73daca'])

    // Render based on chart style
    if (chartStyle === 'heatmap') {
      renderHeatmap(svg, chartData, width, height, margin, x, y, showTooltip, hideTooltip, onSelectType, selectedType)
    } else if (chartStyle === 'scatter') {
      renderScatter(svg, chartData, width, height, margin, x, y, color, types, showTooltip, hideTooltip, onSelectType, selectedType)
    } else {
      renderBubble(svg, chartData, width, height, margin, x, y, color, showTooltip, hideTooltip, onSelectType, selectedType)
    }

    function renderBubble(svg, chartData, width, height, margin, x, y, color, showTooltip, hideTooltip, onSelectType, selectedType) {
      // Quadrant backgrounds
      const quadrants = [
        { x: 0.5, y: 1, label: 'Safe Bets', color: '#9ece6a', opacity: 0.08 },
        { x: 0, y: 1, label: 'Review Needed', color: '#e0af68', opacity: 0.08 },
        { x: 0.5, y: 2.5, label: 'Risky Changes', color: '#f7768e', opacity: 0.08 },
        { x: 0, y: 2.5, label: 'Avoid', color: '#f7768e', opacity: 0.12 },
      ]

      quadrants.forEach(q => {
        svg.append('rect')
          .attr('x', x(q.x))
          .attr('y', y(q.y + 1))
          .attr('width', (width - margin.left - margin.right) / 2)
          .attr('height', (height - margin.top - margin.bottom) / 2)
          .attr('fill', q.color)
          .attr('opacity', q.opacity)
      })

      drawGridAndAxes(svg, width, height, margin, x, y)

      // Points with animation
      svg.selectAll('.point')
        .data(chartData)
        .enter()
        .append('circle')
        .attr('class', 'point')
        .attr('cx', d => x(d.confidence))
        .attr('cy', d => y(d.risk))
        .attr('r', 0)
        .attr('fill', d => color(d.type))
        .attr('stroke', '#1a1b26')
        .attr('stroke-width', 2)
        .style('cursor', 'pointer')
        .on('mouseover', function(event, d) {
          d3.select(this).transition().duration(150).attr('r', 14).attr('stroke-width', 3)
          showRiskTooltip(showTooltip, d, event)
        })
        .on('mouseout', function() {
          d3.select(this).transition().duration(150).attr('r', 10).attr('stroke-width', 2)
          hideTooltip()
        })
        .on('click', (event, d) => onSelectType(selectedType === d.type ? null : d.type))
        .transition().duration(600).delay((d, i) => i * 30).attr('r', 10)

      // Recipe name labels near each bubble
      svg.selectAll('.point-label')
        .data(chartData)
        .enter()
        .append('text')
        .attr('class', 'point-label')
        .attr('x', d => x(d.confidence) + 14)
        .attr('y', d => y(d.risk) + 4)
        .attr('fill', d => color(d.type))
        .attr('font-size', '11px')
        .attr('font-weight', '600')
        .style('pointer-events', 'none')
        .text(d => d.title.length > 28 ? d.title.substring(0, 26) + '…' : d.title)
        .style('opacity', 0)
        .transition().delay(700).duration(400).style('opacity', 0.9)

      drawQuadrantLabels(svg, x, y)
    }

    function renderScatter(svg, chartData, width, height, margin, x, y, color, types, showTooltip, hideTooltip, onSelectType, selectedType) {
      // Light grid background
      svg.append('rect')
        .attr('x', margin.left).attr('y', margin.top)
        .attr('width', width - margin.left - margin.right)
        .attr('height', height - margin.top - margin.bottom)
        .attr('fill', '#1e1f2e')

      drawGridAndAxes(svg, width, height, margin, x, y)

      // Use different symbols for each type
      const symbols = [d3.symbolCircle, d3.symbolSquare, d3.symbolTriangle, d3.symbolDiamond, d3.symbolCross, d3.symbolStar]
      const symbolScale = d3.scaleOrdinal().domain(types).range(symbols)

      svg.selectAll('.point')
        .data(chartData)
        .enter()
        .append('path')
        .attr('class', 'point')
        .attr('transform', d => `translate(${x(d.confidence)},${y(d.risk)})`)
        .attr('d', d => d3.symbol().type(symbolScale(d.type)).size(0)())
        .attr('fill', d => color(d.type))
        .attr('stroke', '#1a1b26')
        .attr('stroke-width', 1.5)
        .style('cursor', 'pointer')
        .on('mouseover', function(event, d) {
          d3.select(this).transition().duration(150)
            .attr('d', d3.symbol().type(symbolScale(d.type)).size(300)())
          showRiskTooltip(showTooltip, d, event)
        })
        .on('mouseout', function(event, d) {
          d3.select(this).transition().duration(150)
            .attr('d', d3.symbol().type(symbolScale(d.type)).size(120)())
          hideTooltip()
        })
        .on('click', (event, d) => onSelectType(selectedType === d.type ? null : d.type))
        .transition().duration(600).delay((d, i) => i * 20)
        .attr('d', d => d3.symbol().type(symbolScale(d.type)).size(120)())

      // Legend for symbols
      const legend = svg.append('g').attr('transform', `translate(${width - margin.right + 10}, ${margin.top})`)
      types.slice(0, 6).forEach((type, i) => {
        const g = legend.append('g').attr('transform', `translate(0, ${i * 22})`)
        g.append('path')
          .attr('d', d3.symbol().type(symbolScale(type)).size(80)())
          .attr('fill', color(type))
        g.append('text')
          .attr('x', 12).attr('y', 4)
          .attr('fill', '#a9b1d6').attr('font-size', '10px')
          .text(type.replace(/_/g, ' ').substring(0, 12))
      })
    }

    function renderHeatmap(svg, chartData, width, height, margin, x, y, showTooltip, hideTooltip, onSelectType, selectedType) {
      // Create bins for heatmap
      const confBins = 5
      const riskBins = 3
      const binWidth = (width - margin.left - margin.right) / confBins
      const binHeight = (height - margin.top - margin.bottom) / riskBins

      // Count items in each cell
      const heatmapData = []
      for (let ci = 0; ci < confBins; ci++) {
        for (let ri = 0; ri < riskBins; ri++) {
          const confMin = ci / confBins
          const confMax = (ci + 1) / confBins
          const riskLevel = ri + 1
          const items = chartData.filter(d => d.confidence >= confMin && d.confidence < confMax && d.risk === riskLevel)
          heatmapData.push({
            ci, ri, confMin, confMax, riskLevel,
            count: items.length,
            items,
            x: margin.left + ci * binWidth,
            y: margin.top + (2 - ri) * binHeight,
          })
        }
      }

      const maxCount = d3.max(heatmapData, d => d.count) || 1
      const colorScale = d3.scaleSequential(d3.interpolateBlues).domain([0, maxCount])

      // Draw cells
      svg.selectAll('.heatmap-cell')
        .data(heatmapData)
        .enter()
        .append('rect')
        .attr('class', 'heatmap-cell')
        .attr('x', d => d.x)
        .attr('y', d => d.y)
        .attr('width', binWidth - 2)
        .attr('height', binHeight - 2)
        .attr('fill', d => d.count > 0 ? colorScale(d.count) : '#1e1f2e')
        .attr('stroke', '#2a2b3d')
        .attr('stroke-width', 1)
        .attr('rx', 4)
        .style('cursor', d => d.count > 0 ? 'pointer' : 'default')
        .on('mouseover', function(event, d) {
          if (d.count === 0) return
          d3.select(this).transition().duration(150).attr('stroke', '#7aa2f7').attr('stroke-width', 2)
          showTooltip(
            <div className="tooltip-content">
              <div className="tooltip-title">{d.count} Recipe{d.count !== 1 ? 's' : ''}</div>
              <div className="tooltip-row">
                <span>Confidence:</span>
                <span>{(d.confMin * 100).toFixed(0)}-{(d.confMax * 100).toFixed(0)}%</span>
              </div>
              <div className="tooltip-row">
                <span>Risk:</span>
                <span>{['Low', 'Medium', 'High'][d.riskLevel - 1]}</span>
              </div>
              {d.items.length > 0 && (
                <div className="tooltip-list">
                  {d.items.slice(0, 3).map((item, i) => (
                    <div key={i} className="tooltip-list-item">{item.displayType}</div>
                  ))}
                  {d.items.length > 3 && <div className="tooltip-list-item">+{d.items.length - 3} more...</div>}
                </div>
              )}
            </div>,
            event
          )
        })
        .on('mouseout', function() {
          d3.select(this).transition().duration(150).attr('stroke', '#2a2b3d').attr('stroke-width', 1)
          hideTooltip()
        })

      // Cell labels
      svg.selectAll('.cell-label')
        .data(heatmapData.filter(d => d.count > 0))
        .enter()
        .append('text')
        .attr('class', 'cell-label')
        .attr('x', d => d.x + binWidth / 2 - 1)
        .attr('y', d => d.y + binHeight / 2)
        .attr('text-anchor', 'middle')
        .attr('dy', '0.35em')
        .attr('fill', d => d.count > maxCount / 2 ? '#1a1b26' : '#c0caf5')
        .attr('font-size', '16px')
        .attr('font-weight', '700')
        .text(d => d.count)

      // Axes labels
      const confLabels = ['0-20%', '20-40%', '40-60%', '60-80%', '80-100%']
      svg.selectAll('.conf-label')
        .data(confLabels)
        .enter()
        .append('text')
        .attr('x', (d, i) => margin.left + i * binWidth + binWidth / 2)
        .attr('y', height - margin.bottom + 20)
        .attr('text-anchor', 'middle')
        .attr('fill', '#787c99')
        .attr('font-size', '11px')
        .text(d => d)

      const riskLabels = ['High', 'Medium', 'Low']
      svg.selectAll('.risk-label')
        .data(riskLabels)
        .enter()
        .append('text')
        .attr('x', margin.left - 10)
        .attr('y', (d, i) => margin.top + i * binHeight + binHeight / 2)
        .attr('text-anchor', 'end')
        .attr('dy', '0.35em')
        .attr('fill', '#787c99')
        .attr('font-size', '11px')
        .text(d => d)

      // Axis titles
      svg.append('text')
        .attr('x', width / 2).attr('y', height - 20)
        .attr('text-anchor', 'middle').attr('fill', '#c0caf5').attr('font-size', '13px')
        .text('Confidence Level')

      svg.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('x', -(height / 2)).attr('y', 20)
        .attr('text-anchor', 'middle').attr('fill', '#c0caf5').attr('font-size', '13px')
        .text('Risk Level')
    }

    function drawGridAndAxes(svg, width, height, margin, x, y) {
      // Grid lines
      svg.selectAll('.grid-x')
        .data([0.25, 0.5, 0.75])
        .enter()
        .append('line')
        .attr('x1', d => x(d)).attr('x2', d => x(d))
        .attr('y1', margin.top).attr('y2', height - margin.bottom)
        .attr('stroke', '#2a2b3d').attr('stroke-dasharray', '3,3')

      svg.selectAll('.grid-y')
        .data([1, 2, 3])
        .enter()
        .append('line')
        .attr('x1', margin.left).attr('x2', width - margin.right)
        .attr('y1', d => y(d)).attr('y2', d => y(d))
        .attr('stroke', '#2a2b3d').attr('stroke-dasharray', '3,3')

      // Axes
      svg.append('g')
        .attr('transform', `translate(0,${height - margin.bottom})`)
        .call(d3.axisBottom(x).ticks(5).tickFormat(d3.format('.0%')))
        .selectAll('text').attr('fill', '#787c99')

      svg.append('g')
        .attr('transform', `translate(${margin.left},0)`)
        .call(d3.axisLeft(y).tickValues([1, 2, 3]).tickFormat(d => ['Low', 'Medium', 'High'][d - 1]))
        .selectAll('text').attr('fill', '#787c99')

      // Axis labels
      svg.append('text')
        .attr('x', width / 2).attr('y', height - 20)
        .attr('text-anchor', 'middle').attr('fill', '#c0caf5').attr('font-size', '13px')
        .text('Confidence Level')

      svg.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('x', -(height / 2)).attr('y', 20)
        .attr('text-anchor', 'middle').attr('fill', '#c0caf5').attr('font-size', '13px')
        .text('Risk Level')
    }

    function drawQuadrantLabels(svg, x, y) {
      svg.append('text')
        .attr('x', x(0.75)).attr('y', y(0.8))
        .attr('text-anchor', 'middle').attr('fill', '#9ece6a')
        .attr('font-size', '12px').attr('font-weight', '600').style('opacity', 0.8)
        .text('Safe Bets ✓')

      svg.append('text')
        .attr('x', x(0.25)).attr('y', y(3.2))
        .attr('text-anchor', 'middle').attr('fill', '#f7768e')
        .attr('font-size', '12px').attr('font-weight', '600').style('opacity', 0.8)
        .text('Needs Review ⚠')
    }

    function showRiskTooltip(showTooltip, d, event) {
      showTooltip(
        <div className="tooltip-content wide">
          <div className="tooltip-title">{d.title}</div>
          <div className="tooltip-row"><span>Category:</span><span>{d.displayType}</span></div>
          <div className="tooltip-row"><span>Confidence:</span><span>{(d.confidence * 100).toFixed(0)}%</span></div>
          <div className="tooltip-row">
            <span>Priority:</span>
            <span className={d.riskLabel === 'high' ? 'danger' : d.riskLabel === 'medium' ? 'warning' : ''}>{d.riskLabel}</span>
          </div>
          {d.proposedClass && <div className="tooltip-row"><span>Proposes:</span><span>{d.proposedClass}</span></div>}
          {d.rationale && <div className="tooltip-description">{d.rationale.substring(0, 130)}{d.rationale.length > 130 ? '…' : ''}</div>}
          {d.risksList?.length > 0 && (
            <div className="tooltip-risks">⚠ {d.risksList.length} risk{d.risksList.length > 1 ? 's' : ''} identified</div>
          )}
          <div className="tooltip-hint">Click to filter by category</div>
        </div>,
        event
      )
    }

  }, [data, selectedType, onSelectType, showTooltip, hideTooltip, chartStyle])

  return (
    <div className="chart-wrapper" ref={containerRef}>
      <div className="chart-header">
        <div className="chart-header-left">
          <h3 className="chart-title">Recipe Confidence vs Risk Matrix</h3>
          <p className="chart-description">Recipes plotted by implementation confidence and risk level</p>
        </div>
      </div>
      <svg ref={svgRef}></svg>
    </div>
  )
}

/**
 * 5. Action Priority - Multiple Styles (Stacked Bar, Treemap, Timeline)
 */
function ActionPriorityChart({ data, selectedPriority, onSelectPriority, showTooltip, hideTooltip, chartStyle, onStyleChange, availableStyles }) {
  const svgRef = useRef()
  const containerRef = useRef()

  // Build full recommendation groups for the detail panel (React-driven)
  const recGroups = useMemo(() => {
    const recs = data?.recommendations || []
    return {
      1: recs.filter(a => a.priority === 'high'),
      2: recs.filter(a => a.priority === 'medium'),
      3: recs.filter(a => a.priority === 'low'),
    }
  }, [data])

  useEffect(() => {
    if (!svgRef.current) return

    // Map AI recommendations (string priorities) to action-like items
    const recommendations = data?.recommendations || []
    const byPriority = {
      1: recommendations.filter(a => a.priority === 'high').map(a => ({ action: a.title, description: a.description })),
      2: recommendations.filter(a => a.priority === 'medium').map(a => ({ action: a.title, description: a.description })),
      3: recommendations.filter(a => a.priority === 'low').map(a => ({ action: a.title, description: a.description })),
    }

    const priorityLabels = {
      1: 'Priority 1 (High)',
      2: 'Priority 2 (Medium)',
      3: 'Priority 3 (Low)',
    }

    const hierarchyData = {
      name: 'Actions',
      children: Object.entries(byPriority)
        .filter(([, items]) => items.length > 0)
        .map(([priority, items]) => ({
          name: priorityLabels[priority],
          priority: parseInt(priority),
          children: items.map(a => ({
            name: a.action,
            value: 4 - parseInt(priority),
            description: a.description,
            className: null,
            priority: parseInt(priority),
          })),
        })),
    }

    if (hierarchyData.children.length === 0) {
      d3.select(svgRef.current).selectAll('*').remove()
      const container = containerRef.current
      d3.select(svgRef.current)
        .attr('width', container.clientWidth)
        .attr('height', container.clientHeight)
        .append('text')
        .attr('x', container.clientWidth / 2)
        .attr('y', container.clientHeight / 2)
        .attr('text-anchor', 'middle')
        .attr('fill', '#787c99')
        .text('No action items detected')
      return
    }

    const container = containerRef.current
    const width = container.clientWidth
    const height = container.clientHeight
    const margin = { top: 10, right: 10, bottom: 10, left: 10 }

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    svg.attr('width', width).attr('height', height)

    const color = d3.scaleOrdinal()
      .domain([1, 2, 3])
      .range(['#f7768e', '#e0af68', '#9ece6a'])

    // Render based on chart style
    if (chartStyle === 'horizontal-stacked') {
      renderStackedBar(svg, byPriority, priorityLabels, width, height, margin, color, selectedPriority, onSelectPriority, showTooltip, hideTooltip)
    } else if (chartStyle === 'timeline') {
      renderTimeline(svg, byPriority, priorityLabels, width, height, margin, color, selectedPriority, onSelectPriority, showTooltip, hideTooltip)
    } else {
      renderTreemapActions(svg, hierarchyData, width, height, margin, color, selectedPriority, onSelectPriority, showTooltip, hideTooltip)
    }

    function renderTreemapActions(svg, hierarchyData, width, height, margin, color, selectedPriority, onSelectPriority, showTooltip, hideTooltip) {
      const treemap = d3.treemap()
        .size([width - margin.left - margin.right, height - margin.top - margin.bottom])
        .paddingOuter(4)
        .paddingTop(24)
        .paddingInner(3)
        .round(true)

      const root = d3.hierarchy(hierarchyData)
        .sum(d => d.value)
        .sort((a, b) => b.value - a.value)

      treemap(root)

      const g = svg.append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`)

      // Group containers
      const groups = g.selectAll('.group')
        .data(root.children || [])
        .enter()
        .append('g')
        .attr('class', 'group')
        .style('opacity', d => selectedPriority && selectedPriority !== d.data.priority ? 0.3 : 1)
        .style('cursor', 'pointer')
        .on('click', function(event, d) {
          onSelectPriority(selectedPriority === d.data.priority ? null : d.data.priority)
        })

      groups.append('rect')
        .attr('x', d => d.x0)
        .attr('y', d => d.y0)
        .attr('width', d => d.x1 - d.x0)
        .attr('height', d => d.y1 - d.y0)
        .attr('fill', d => color(d.data.priority))
        .attr('fill-opacity', 0.2)
        .attr('stroke', d => color(d.data.priority))
        .attr('stroke-width', 2)
        .attr('rx', 6)

      groups.append('text')
        .attr('x', d => d.x0 + 8)
        .attr('y', d => d.y0 + 17)
        .attr('fill', d => color(d.data.priority))
        .attr('font-size', '12px')
        .attr('font-weight', '700')
        .text(d => `${d.data.name} (${d.children?.length || 0})`)

      // Leaf rectangles
      const leaves = g.selectAll('.leaf')
        .data(root.leaves())
        .enter()
        .append('g')
        .attr('class', 'leaf')
        .attr('transform', d => `translate(${d.x0},${d.y0})`)
        .style('cursor', 'pointer')
        .on('mouseover', function(event, d) {
          d3.select(this).select('rect').transition().duration(150).attr('fill-opacity', 1).attr('stroke-width', 2)
          showActionTooltip(showTooltip, d, event)
        })
        .on('mouseout', function() {
          d3.select(this).select('rect').transition().duration(150).attr('fill-opacity', 0.85).attr('stroke-width', 0)
          hideTooltip()
        })

      leaves.append('rect')
        .attr('width', d => Math.max(0, d.x1 - d.x0))
        .attr('height', d => Math.max(0, d.y1 - d.y0))
        .attr('fill', d => color(d.data.priority))
        .attr('fill-opacity', 0.85)
        .attr('stroke', '#1a1b26')
        .attr('stroke-width', 0)
        .attr('rx', 4)

      leaves.append('text')
        .attr('x', 6)
        .attr('y', 16)
        .attr('fill', 'white')
        .attr('font-size', '11px')
        .attr('font-weight', '500')
        .each(function(d) {
          const text = d.data.name
          const availableWidth = d.x1 - d.x0 - 12
          const self = d3.select(this)
          if (availableWidth < 40) self.text('')
          else if (text.length * 6 > availableWidth) self.text(text.substring(0, Math.floor(availableWidth / 6) - 2) + '...')
          else self.text(text)
        })
    }

    function renderStackedBar(svg, byPriority, priorityLabels, width, height, margin, color, selectedPriority, onSelectPriority, showTooltip, hideTooltip) {
      const stackMargin = { top: 50, right: 30, bottom: 50, left: 150 }
      const innerHeight = height - stackMargin.top - stackMargin.bottom
      const innerWidth = width - stackMargin.left - stackMargin.right

      // Prepare data for stacked bar
      const priorities = [1, 2, 3].filter(p => byPriority[p]?.length > 0)
      const maxItems = Math.max(...priorities.map(p => byPriority[p].length))

      const y = d3.scaleBand()
        .domain(priorities.map(p => priorityLabels[p]))
        .range([0, innerHeight])
        .padding(0.3)

      const x = d3.scaleLinear()
        .domain([0, maxItems])
        .range([0, innerWidth])

      const g = svg.append('g').attr('transform', `translate(${stackMargin.left},${stackMargin.top})`)

      // Background track
      g.selectAll('.track')
        .data(priorities)
        .enter()
        .append('rect')
        .attr('class', 'track')
        .attr('x', 0)
        .attr('y', p => y(priorityLabels[p]))
        .attr('width', innerWidth)
        .attr('height', y.bandwidth())
        .attr('fill', '#1e1f2e')
        .attr('rx', 6)

      // Stacked segments for each item
      priorities.forEach(priority => {
        const items = byPriority[priority]
        const segmentWidth = items.length > 0 ? x(items.length) / items.length : 0
        const barY = y(priorityLabels[priority])

        g.selectAll(`.bar-${priority}`)
          .data(items)
          .enter()
          .append('rect')
          .attr('class', `bar-${priority}`)
          .attr('x', (d, i) => i * segmentWidth)
          .attr('y', barY)
          .attr('width', 0)
          .attr('height', y.bandwidth())
          .attr('fill', color(priority))
          .attr('stroke', '#1a1b26')
          .attr('stroke-width', 1)
          .attr('rx', 2)
          .style('opacity', selectedPriority && selectedPriority !== priority ? 0.3 : 1)
          .style('cursor', 'pointer')
          .on('mouseover', function(event, d) {
            d3.select(this).transition().duration(150).attr('fill-opacity', 0.8)
            showTooltip(
              <div className="tooltip-content">
                <div className="tooltip-title">{d.action}</div>
                {d.description && <div className="tooltip-description">{d.description}</div>}
                <div className="tooltip-row">
                  <span>Priority:</span>
                  <span className={priority === 1 ? 'danger' : priority === 2 ? 'warning' : ''}>
                    {priority === 1 ? 'Critical' : priority === 2 ? 'Important' : 'Nice to Have'}
                  </span>
                </div>
              </div>,
              event
            )
          })
          .on('mouseout', function() {
            d3.select(this).transition().duration(150).attr('fill-opacity', 1)
            hideTooltip()
          })
          .on('click', () => onSelectPriority(selectedPriority === priority ? null : priority))
          .transition().duration(600).delay((d, i) => i * 30)
          .attr('width', segmentWidth - 2)
      })

      // Action name labels inside each segment
      priorities.forEach(priority => {
        const items = byPriority[priority]
        const segmentWidth = items.length > 0 ? x(items.length) / items.length : 0
        const barY = y(priorityLabels[priority])

        g.selectAll(`.seg-label-${priority}`)
          .data(items)
          .enter()
          .append('text')
          .attr('class', `seg-label-${priority}`)
          .attr('x', (d, i) => i * segmentWidth + 8)
          .attr('y', barY + y.bandwidth() / 2)
          .attr('dy', '0.35em')
          .attr('fill', 'rgba(26,27,38,0.92)')
          .attr('font-size', '11px')
          .attr('font-weight', '600')
          .style('pointer-events', 'none')
          .each(function(d) {
            const maxChars = Math.floor((segmentWidth - 16) / 6.5)
            const label = maxChars < 6 ? '' : d.action.length > maxChars ? d.action.substring(0, maxChars - 2) + '…' : d.action
            d3.select(this).text(label)
          })
          .style('opacity', 0)
          .transition().delay(700).duration(300).style('opacity', 1)
      })

      // Count labels at end of bars
      g.selectAll('.count-label')
        .data(priorities)
        .enter()
        .append('text')
        .attr('x', p => x(byPriority[p].length) + 8)
        .attr('y', p => y(priorityLabels[p]) + y.bandwidth() / 2)
        .attr('dy', '0.35em')
        .attr('fill', '#c0caf5')
        .attr('font-size', '14px')
        .attr('font-weight', '700')
        .text(p => byPriority[p].length)
        .style('opacity', 0)
        .transition().delay(600).duration(300).style('opacity', 1)

      // Y axis
      svg.append('g')
        .attr('transform', `translate(${stackMargin.left},${stackMargin.top})`)
        .call(d3.axisLeft(y).tickSize(0))
        .selectAll('text')
        .attr('fill', '#c0caf5')
        .attr('font-size', '12px')
        .style('cursor', 'pointer')
        .on('click', function(event, label) {
          const priority = priorities.find(p => priorityLabels[p] === label)
          if (priority) onSelectPriority(selectedPriority === priority ? null : priority)
        })

      svg.select('.domain').remove()

      // Title
      svg.append('text')
        .attr('x', width / 2).attr('y', 25)
        .attr('text-anchor', 'middle')
        .attr('fill', '#c0caf5').attr('font-size', '14px').attr('font-weight', '600')
        .text('Actions by Priority')
    }

    function renderTimeline(svg, byPriority, priorityLabels, width, height, margin, color, selectedPriority, onSelectPriority, showTooltip, hideTooltip) {
      const timelineMargin = { top: 40, right: 30, bottom: 30, left: 30 }
      const innerHeight = height - timelineMargin.top - timelineMargin.bottom
      const innerWidth = width - timelineMargin.left - timelineMargin.right

      const g = svg.append('g').attr('transform', `translate(${timelineMargin.left},${timelineMargin.top})`)

      // Create timeline phases
      const phases = [
        { priority: 1, label: 'Critical', x: 0, width: innerWidth * 0.4 },
        { priority: 2, label: 'Important', x: innerWidth * 0.4, width: innerWidth * 0.35 },
        { priority: 3, label: 'Nice to Have', x: innerWidth * 0.75, width: innerWidth * 0.25 },
      ]

      // Phase backgrounds
      phases.forEach((phase, i) => {
        g.append('rect')
          .attr('x', phase.x)
          .attr('y', 0)
          .attr('width', phase.width - 4)
          .attr('height', innerHeight)
          .attr('fill', color(phase.priority))
          .attr('fill-opacity', 0.1)
          .attr('stroke', color(phase.priority))
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '4,4')
          .attr('rx', 8)
          .style('cursor', 'pointer')
          .style('opacity', selectedPriority && selectedPriority !== phase.priority ? 0.3 : 1)
          .on('click', () => onSelectPriority(selectedPriority === phase.priority ? null : phase.priority))

        // Phase header
        g.append('text')
          .attr('x', phase.x + phase.width / 2 - 2)
          .attr('y', -10)
          .attr('text-anchor', 'middle')
          .attr('fill', color(phase.priority))
          .attr('font-size', '12px')
          .attr('font-weight', '700')
          .text(`${phase.label} (${byPriority[phase.priority]?.length || 0})`)
      })

      // Timeline arrow
      g.append('line')
        .attr('x1', 0).attr('y1', innerHeight + 15)
        .attr('x2', innerWidth).attr('y2', innerHeight + 15)
        .attr('stroke', '#565f89').attr('stroke-width', 2)
        .attr('marker-end', 'url(#arrow)')

      // Arrow marker
      svg.append('defs').append('marker')
        .attr('id', 'arrow')
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 8).attr('refY', 0)
        .attr('markerWidth', 6).attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,-5L10,0L0,5')
        .attr('fill', '#565f89')

      // Render items within each phase
      phases.forEach(phase => {
        const items = byPriority[phase.priority] || []
        const itemHeight = Math.min(32, (innerHeight - 20) / Math.max(items.length, 1))
        const itemWidth = phase.width - 20

        items.forEach((item, i) => {
          const itemX = phase.x + 10
          const itemY = 20 + i * (itemHeight + 4)

          if (itemY + itemHeight > innerHeight) return // Skip if overflow

          const itemG = g.append('g')
            .attr('transform', `translate(${itemX},${itemY})`)
            .style('cursor', 'pointer')
            .style('opacity', selectedPriority && selectedPriority !== phase.priority ? 0.3 : 1)
            .on('mouseover', function(event) {
              d3.select(this).select('rect').transition().duration(150).attr('fill-opacity', 1)
              showTooltip(
                <div className="tooltip-content">
                  <div className="tooltip-title">{item.action}</div>
                  {item.description && <div className="tooltip-description">{item.description}</div>}
                  <div className="tooltip-row">
                    <span>Priority:</span>
                    <span className={phase.priority === 1 ? 'danger' : phase.priority === 2 ? 'warning' : ''}>
                      {phase.label}
                    </span>
                  </div>
                </div>,
                event
              )
            })
            .on('mouseout', function() {
              d3.select(this).select('rect').transition().duration(150).attr('fill-opacity', 0.85)
              hideTooltip()
            })
            .on('click', () => onSelectPriority(selectedPriority === phase.priority ? null : phase.priority))

          itemG.append('rect')
            .attr('width', itemWidth - 4)
            .attr('height', itemHeight - 2)
            .attr('fill', color(phase.priority))
            .attr('fill-opacity', 0.85)
            .attr('rx', 4)

          itemG.append('text')
            .attr('x', 6)
            .attr('y', itemHeight / 2)
            .attr('dy', '0.35em')
            .attr('fill', 'white')
            .attr('font-size', '10px')
            .text(item.action.length > (itemWidth / 6) ? item.action.substring(0, Math.floor(itemWidth / 6) - 2) + '...' : item.action)
        })
      })
    }

    function showActionTooltip(showTooltip, d, event) {
      showTooltip(
        <div className="tooltip-content">
          <div className="tooltip-title">{d.data.name}</div>
          {d.data.description && <div className="tooltip-description">{d.data.description}</div>}
          {d.data.className && <div className="tooltip-row"><span>Class:</span><span>{d.data.className}</span></div>}
          <div className="tooltip-row">
            <span>Priority:</span>
            <span className={d.data.priority === 1 ? 'danger' : d.data.priority === 2 ? 'warning' : ''}>
              {d.data.priority === 1 ? 'Critical' : d.data.priority === 2 ? 'Important' : 'Nice to Have'}
            </span>
          </div>
        </div>,
        event
      )
    }

  }, [data, selectedPriority, onSelectPriority, showTooltip, hideTooltip, chartStyle])

  const priorityColors = { 1: '#f7768e', 2: '#e0af68', 3: '#9ece6a' }
  const priorityNames = { 1: 'High Priority', 2: 'Medium Priority', 3: 'Low Priority' }

  return (
    <div className="chart-wrapper action-chart-wrapper" ref={containerRef}>
      <div className="chart-header">
        <div className="chart-header-left">
          <h3 className="chart-title">AI Recommendations by Priority</h3>
          <p className="chart-description">Click a priority group in the chart to filter — details below</p>
        </div>
      </div>
      <svg ref={svgRef}></svg>

      {/* React-driven detail panel — always visible, shows all recommendations */}
      <div className="action-items-panel">
        {[1, 2, 3].filter(p => recGroups[p].length > 0).map(p => (
          <div
            key={p}
            className={`apg ${selectedPriority === p ? 'apg--selected' : ''}`}
            style={{ '--apg-color': priorityColors[p] }}
          >
            <div className="apg-header" onClick={() => onSelectPriority(selectedPriority === p ? null : p)}>
              <span className="apg-label" style={{ color: priorityColors[p] }}>{priorityNames[p]}</span>
              <span className="apg-count" style={{ background: priorityColors[p] + '22', color: priorityColors[p] }}>
                {recGroups[p].length}
              </span>
            </div>
            <div className="apg-items">
              {recGroups[p].map((rec, i) => (
                <div key={i} className="apg-item">
                  <div className="apg-item-title">{rec.title}</div>
                  <div className="apg-item-meta">
                    {rec.confidence != null && (
                      <span className="apg-badge confidence">{(rec.confidence * 100).toFixed(0)}% confidence</span>
                    )}
                    {rec.risks?.length > 0 && (
                      <span className="apg-badge risk">{rec.risks.length} risk{rec.risks.length > 1 ? 's' : ''}</span>
                    )}
                    {rec.category && (
                      <span className="apg-badge cat">{rec.category.replace(/_/g, ' ')}</span>
                    )}
                  </div>
                  {rec.rationale && (
                    <div className="apg-rationale">{rec.rationale}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * 6. Details Grid - Searchable, Filterable, Exportable Data Table
 */
function DetailsGrid({ recipesData, aiData, patternsData, selectedRecipeType, selectedClass, selectedPriority }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortConfig, setSortConfig] = useState({ key: 'priority', direction: 'asc' })
  const [activeTab, setActiveTab] = useState('recipes')
  const [expandedRows, setExpandedRows] = useState(new Set())

  // Prepare grid data
  const gridData = useMemo(() => {
    if (activeTab === 'recipes' && recipesData?.recipes) {
      return recipesData.recipes.map((r, idx) => ({
        id: r.recipe_id || `recipe-${idx}`,
        type: r.category?.replace(/_/g, ' ') || 'Unknown',
        rawType: r.category,
        title: r.name || r.category,
        description: r.description || '',
        confidence: r.confidence || 0,
        risk: r.priority || 'medium',
        className: r.steps?.[0]?.new_class || '-',
        priority: r.priority === 'high' ? 1 : r.priority === 'medium' ? 2 : 3,
      }))
    } else if (activeTab === 'actions' && aiData?.recommendations) {
      return aiData.recommendations.map((a, idx) => ({
        id: a.id || `action-${idx}`,
        action: a.title,
        description: a.description || '',
        priority: a.priority === 'high' ? 1 : a.priority === 'medium' ? 2 : 3,
        className: a.proposed_changes?.[0]?.new_class || '-',
      }))
    } else if (activeTab === 'patterns' && patternsData?.patterns) {
      return patternsData.patterns.map((p, idx) => ({
        id: `pattern-${idx}`,
        type: p.pattern_type?.replace(/_/g, ' ') || 'Unknown',
        rawType: p.pattern_type,
        location: p.location?.prefix || p.location?.file || '-',
        severity: p.severity || 'medium',
        details: p.details || '',
        evidenceObj: p.evidence || {},
        suggestedRecipe: p.suggested_recipe || '',
      }))
    }
    return []
  }, [activeTab, recipesData, aiData, patternsData])

  // Filter data
  const filteredData = useMemo(() => {
    let result = [...gridData]

    // Apply search
    if (searchTerm) {
      const lower = searchTerm.toLowerCase()
      result = result.filter(row =>
        Object.values(row).some(val =>
          String(val).toLowerCase().includes(lower)
        )
      )
    }

    // Apply cross-chart filters
    if (selectedRecipeType && activeTab === 'recipes') {
      result = result.filter(row => row.rawType === selectedRecipeType)
    }
    if (selectedClass) {
      result = result.filter(row => row.className === selectedClass)
    }
    if (selectedPriority) {
      result = result.filter(row => row.priority === selectedPriority)
    }

    // Apply sort
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key]
        const bVal = b[sortConfig.key]
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }

    return result
  }, [gridData, searchTerm, selectedRecipeType, selectedClass, selectedPriority, sortConfig])

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }))
  }

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  // Export functions
  const exportToCSV = () => {
    const headers = Object.keys(filteredData[0] || {}).join(',')
    const rows = filteredData.map(row =>
      Object.values(row).map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
    )
    const csv = [headers, ...rows].join('\n')
    downloadFile(csv, `refactor-${activeTab}-${Date.now()}.csv`, 'text/csv')
  }

  const exportToJSON = () => {
    const json = JSON.stringify(filteredData, null, 2)
    downloadFile(json, `refactor-${activeTab}-${Date.now()}.json`, 'application/json')
  }

  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  const getColumns = () => {
    switch (activeTab) {
      case 'recipes':
        return [
          { key: 'type', label: 'Category', width: '18%' },
          { key: 'title', label: 'Name', width: '30%' },
          { key: 'confidence', label: 'Confidence', width: '12%', render: v => `${(v * 100).toFixed(0)}%` },
          { key: 'risk', label: 'Priority', width: '10%', render: v => (
            <span className={`risk-badge ${v}`}>{v}</span>
          )},
          { key: 'className', label: 'Proposed Class', width: '18%' },
        ]
      case 'actions':
        return [
          { key: 'action', label: 'Action', width: '30%' },
          { key: 'priority', label: 'Priority', width: '15%', render: v => (
            <span className={`priority-badge p${v}`}>
              {v === 1 ? 'Critical' : v === 2 ? 'Important' : 'Nice to Have'}
            </span>
          )},
          { key: 'className', label: 'Class', width: '20%' },
        ]
      case 'patterns':
        return [
          { key: 'type', label: 'Pattern Type', width: '20%' },
          { key: 'location', label: 'Location', width: '20%' },
          { key: 'severity', label: 'Severity', width: '12%', render: v => (
            <span className={`severity-badge ${v}`}>{v}</span>
          )},
          { key: 'className', label: 'Class', width: '15%' },
        ]
      default:
        return []
    }
  }

  const columns = getColumns()

  return (
    <div className="details-grid-wrapper">
      <div className="grid-header">
        <h3 className="chart-title">Detailed Analysis Grid</h3>

        <div className="grid-tabs">
          <button
            className={`grid-tab ${activeTab === 'recipes' ? 'active' : ''}`}
            onClick={() => setActiveTab('recipes')}
          >
            Recipes ({recipesData?.recipes?.length || 0})
          </button>
          <button
            className={`grid-tab ${activeTab === 'actions' ? 'active' : ''}`}
            onClick={() => setActiveTab('actions')}
          >
            AI Recommendations ({aiData?.recommendations?.length || 0})
          </button>
          <button
            className={`grid-tab ${activeTab === 'patterns' ? 'active' : ''}`}
            onClick={() => setActiveTab('patterns')}
          >
            Patterns ({patternsData?.patterns?.length || 0})
          </button>
        </div>

        <div className="grid-controls">
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm('')}>
                <X size={14} />
              </button>
            )}
          </div>

          <div className="export-buttons">
            <button onClick={exportToCSV} title="Export to CSV">
              <FileSpreadsheet size={16} />
              CSV
            </button>
            <button onClick={exportToJSON} title="Export to JSON">
              <FileJson size={16} />
              JSON
            </button>
          </div>
        </div>
      </div>

      <div className="grid-info">
        Showing {filteredData.length} of {gridData.length} items
      </div>

      <div className="grid-table-container">
        <table className="grid-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              {columns.map(col => (
                <th
                  key={col.key}
                  style={{ width: col.width }}
                  onClick={() => handleSort(col.key)}
                  className={sortConfig.key === col.key ? 'sorted' : ''}
                >
                  <span>{col.label}</span>
                  {sortConfig.key === col.key && (
                    sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="no-data">
                  No data matches your filters
                </td>
              </tr>
            ) : (
              filteredData.map(row => (
                <>
                  <tr key={row.id} className={expandedRows.has(row.id) ? 'expanded' : ''}>
                    <td className="expand-cell">
                      <button onClick={() => toggleRow(row.id)}>
                        {expandedRows.has(row.id) ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                    </td>
                    {columns.map(col => (
                      <td key={col.key}>
                        {col.render ? col.render(row[col.key]) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                  {expandedRows.has(row.id) && (
                    <tr key={`${row.id}-details`} className="details-row">
                      <td colSpan={columns.length + 1}>
                        <div className="row-details">
                          {row.description && (
                            <div className="detail-item">
                              <strong>Description:</strong>
                              <p>{row.description}</p>
                            </div>
                          )}
                          {row.details && (
                            <div className="detail-item">
                              <strong>Details:</strong>
                              <p>{row.details}</p>
                            </div>
                          )}
                          {row.evidenceObj && Object.keys(row.evidenceObj).length > 0 && (
                            <div className="detail-item">
                              <strong>Evidence:</strong>
                              <div className="evidence-structured">
                                {Object.entries(row.evidenceObj).map(([key, val]) => (
                                  <div key={key} className="evidence-row">
                                    <span className="evidence-key">{key.replace(/_/g, ' ')}</span>
                                    <span className="evidence-val">
                                      {Array.isArray(val)
                                        ? val.slice(0, 6).join(', ') + (val.length > 6 ? ` … +${val.length - 6} more` : '')
                                        : String(val)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {row.suggestedRecipe && (
                            <div className="detail-item">
                              <strong>Suggested Recipe:</strong>
                              <code>{row.suggestedRecipe}</code>
                            </div>
                          )}
                          <div className="detail-item">
                            <strong>ID:</strong>
                            <code>{row.id}</code>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * Side Panel - Summary stats and details
 */
function SidePanel({ recipesData, aiData, patternsData, activeChart, selectedRecipeType, selectedPriority }) {
  const recipes = recipesData?.recipes || []
  const patterns = patternsData?.patterns || []
  const actions = aiData?.recommendations || []

  // Calculate summary stats
  const totalRecipes = recipes.length
  const highRiskRecipes = recipes.filter(r => r.priority === 'high').length
  const mediumRiskRecipes = recipes.filter(r => r.priority === 'medium').length
  const lowRiskRecipes = recipes.filter(r => r.priority === 'low').length
  const avgConfidence = recipes.length > 0
    ? (recipes.reduce((sum, r) => sum + (r.confidence || 0), 0) / recipes.length * 100).toFixed(0)
    : 0

  const criticalActions = actions.filter(a => a.priority === 'high').length
  const totalPatterns = patterns.length
  const complexityPatterns = patterns.filter(p => p.pattern_type === 'high_complexity_method').length

  // Get top recipes by category
  const recipesByType = {}
  recipes.forEach(r => {
    const type = r.category || 'unknown'
    recipesByType[type] = (recipesByType[type] || 0) + 1
  })
  const topTypes = Object.entries(recipesByType)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)

  // Get recent/important items for quick view
  const importantRecipes = recipes
    .filter(r => r.priority === 'high' || r.confidence >= 0.8)
    .slice(0, 5)

  return (
    <div className="side-panel">
      {/* Summary Stats */}
      <div className="panel-section">
        <h4 className="panel-section-title">
          <Activity size={16} />
          Summary
        </h4>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{totalRecipes}</div>
            <div className="stat-label">Total Recipes</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{avgConfidence}%</div>
            <div className="stat-label">Avg Confidence</div>
          </div>
          <div className="stat-card warning">
            <div className="stat-value">{criticalActions}</div>
            <div className="stat-label">Critical Actions</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{totalPatterns}</div>
            <div className="stat-label">Patterns Found</div>
          </div>
        </div>
      </div>

      {/* Risk Breakdown */}
      <div className="panel-section">
        <h4 className="panel-section-title">
          <AlertTriangle size={16} />
          Risk Breakdown
        </h4>
        <div className="risk-bars">
          <div className="risk-bar-item">
            <div className="risk-bar-header">
              <span className="risk-label high">High Risk</span>
              <span className="risk-count">{highRiskRecipes}</span>
            </div>
            <div className="risk-bar-track">
              <div
                className="risk-bar-fill high"
                style={{ width: `${totalRecipes > 0 ? (highRiskRecipes / totalRecipes) * 100 : 0}%` }}
              />
            </div>
          </div>
          <div className="risk-bar-item">
            <div className="risk-bar-header">
              <span className="risk-label medium">Medium Risk</span>
              <span className="risk-count">{mediumRiskRecipes}</span>
            </div>
            <div className="risk-bar-track">
              <div
                className="risk-bar-fill medium"
                style={{ width: `${totalRecipes > 0 ? (mediumRiskRecipes / totalRecipes) * 100 : 0}%` }}
              />
            </div>
          </div>
          <div className="risk-bar-item">
            <div className="risk-bar-header">
              <span className="risk-label low">Low Risk</span>
              <span className="risk-count">{lowRiskRecipes}</span>
            </div>
            <div className="risk-bar-track">
              <div
                className="risk-bar-fill low"
                style={{ width: `${totalRecipes > 0 ? (lowRiskRecipes / totalRecipes) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Top Recipe Types */}
      <div className="panel-section">
        <h4 className="panel-section-title">
          <Layers size={16} />
          Top Recipe Types
        </h4>
        <div className="type-list">
          {topTypes.map(([type, count]) => (
            <div
              key={type}
              className={`type-item ${selectedRecipeType === type ? 'selected' : ''}`}
            >
              <span className="type-name">{type.replace(/_/g, ' ')}</span>
              <span className="type-count">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Key Insights */}
      <div className="panel-section">
        <h4 className="panel-section-title">
          <Zap size={16} />
          Key Insights
        </h4>
        <div className="insights-list">
          {complexityPatterns > 0 && (
            <div className="insight-item">
              <Code2 size={14} />
              <span>{complexityPatterns} high complexity methods need refactoring</span>
            </div>
          )}
          {highRiskRecipes > 0 && (
            <div className="insight-item warning">
              <AlertTriangle size={14} />
              <span>{highRiskRecipes} high-risk changes require careful review</span>
            </div>
          )}
          {lowRiskRecipes > 0 && (
            <div className="insight-item success">
              <CheckCircle2 size={14} />
              <span>{lowRiskRecipes} safe changes ready to implement</span>
            </div>
          )}
          {criticalActions > 0 && (
            <div className="insight-item warning">
              <Clock size={14} />
              <span>{criticalActions} critical action items pending</span>
            </div>
          )}
        </div>
      </div>

      {/* High Priority Items */}
      {importantRecipes.length > 0 && (
        <div className="panel-section">
          <h4 className="panel-section-title">
            <TrendingUp size={16} />
            Priority Items
          </h4>
          <div className="priority-list">
            {importantRecipes.map((recipe, idx) => (
              <div key={idx} className="priority-item">
                <div className="priority-item-header">
                  <span className={`risk-dot ${recipe.priority}`} />
                  <span className="priority-item-type">
                    {recipe.category?.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="priority-item-title">
                  {recipe.name || recipe.category}
                </div>
                <div className="priority-item-meta">
                  <span>{(recipe.confidence * 100).toFixed(0)}% confidence</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default RefactorReportView
